import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { AlertTriangle, HelpCircle } from 'lucide-react';
import { parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

import ScorecardNavigation from '../ScorecardNavigation';
import FullPageSpinner from '../../FullPageSpinner';
import { apiCall } from '../RestaurantBackend';
import { useAuth } from '../../../contexts/AuthContext';
import {
  WINDOW_OPTIONS,
  DEFAULT_WINDOW,
  WindowDays,
  LocationOption,
  prettyType,
  isReviewType,
  isJudgedType,
  formatClock,
  disagreementChipLabel,
  // feat-186 — queue composition control
  BucketShares,
  ReviewBucket,
  REVIEW_BUCKETS,
  BUCKET_SEGMENT_CLASS,
  BUCKET_LABEL,
  COMPOSITION_PRESETS,
  CompositionPreset,
  DEFAULT_SHARES,
  normalizeShares,
  matchPreset,
  agreementMaxDaysOptions,
  generateAgreementSeed,
  parseSharesParam,
  sharesToParam,
  isReviewBucket,
} from './interactionEvaluationsQueries';
import {
  getInteractionReviewList,
  getInteractionReviewById,
  getInteractionEvalLocations,
  InteractionReviewItem,
  ReviewJudgement,
} from '../../../services/scopedReadsService';

// feat-159 → feat-185 — type-first interaction review. The screen was a FLASHCARD
// (one interaction per viewport, ←/→ paging); feat-185 makes it a scrollable STACK
// of small rows, because the goal is volume: read the transcript, glance at three
// aligned verdict chips, click once, keep scrolling.
//
// Design: Variant C — flush list + fixed click column
// (`.team/features/feat-185-review-stack-rapid-verification/design/variant-c-flush-list.html`,
// revision 2). One continuous panel with hairline dividers (no per-card chrome),
// the three verdict sources in an aligned rail, and every row's Correct/Undecided/
// Incorrect at the same x in a fixed right gutter.
//
// What each row shows (D4, as AMENDED by Preston 2026-07-23): `View shift ↗`,
// disagreement chips, the transcript as BARE TEXT — no `span MM:SS–MM:SS` header,
// no `[MM:SS]` clock prefix, no `Speaker N:` prefix — the three-source evidence
// rail, and the three-way verdict toggle. Employee name / location / date-time are
// gone (D4: shorter row, and it removes an identity-bias source from the grading
// decision). Reasoning prose renders only on expand. The "Judge rubric" chip grid
// is gone entirely (D9) — collapsed, expanded, anywhere.
//
// The write is unchanged: the existing feat-131 verify write
// (PUT /shift-interactions/{id}), optimistic, `true` / `false` / `null` for
// Correct / Incorrect / Undecided. Admin-only, cross-tenant.

const PAGE_SIZE = 25; // ADR 0001 D4 server default; page next when count == limit

// feat-183 (ADR 0001 D1) — highlight-predicate tolerance. The backend returns the
// TIGHT window [floor(start_time), ceil(end_time)] — exactly the interaction span
// the judge scored (the old feat-161 [start−240 → end+30] window is gone). The
// predicate marks the span [start_time, end_time]. shift_interaction.start_time/
// end_time are single-precision FLOAT columns, so a boundary transcript part can
// land a hair past a rounded bound (e.g. part 1414.34 vs end_time 1414.30) — the
// same clip that dropped the concluding line server-side. A 2s epsilon on both
// bounds absorbs that sub-second drift. Named, not a literal. MOVED VERBATIM from
// the flashcard implementation — feat-161's reason is real; do not "simplify" it.
const SPAN_EPS = 2; // seconds

/**
 * Is this transcript part inside the interaction span (± SPAN_EPS)? Pure, exported
 * so the feat-161 boundary tolerance stays pinned by a test independently of
 * whether it is currently RENDERED (see RENDER_IN_SPAN_RULE).
 */
export const isInSpan = (
  partStart: number,
  spanStart: number,
  spanEnd: number | null,
): boolean =>
  partStart >= spanStart - SPAN_EPS && (spanEnd == null || partStart <= spanEnd + SPAN_EPS);

// feat-185 (spec §3) — the per-row coral in-span rule is DROPPED from rendering.
// Post-feat-183 the returned window IS the interaction span, so the rule already
// marked ~100% of rows; with the clocks and speaker labels also gone (D4 amended)
// it is decoration on plain text. Variant C marks the WHOLE transcript block with
// one coral left rule instead — which is what "the block is the scored span"
// literally means now. This is a RENDERING decision only: the predicate above is
// untouched, and flipping this flag to `true` restores the per-row rule. Deliberate
// one-line reversibility, as the spec requires.
const RENDER_IN_SPAN_RULE = false;

// feat-185 (spec §3) — collapsed transcript cap. Past this, a `+K more lines`
// button expands the transcript IN PLACE. Never an internal scrollbar: scrolling
// inside a card is the flashcard-era pain, and it does not survive into the stack.
const COLLAPSED_TRANSCRIPT_ROWS = 10;

// DB timestamps come back zone-less; treat as UTC and render Pacific (matches
// InteractionEvaluations / RecentShiftsTable).
const formatDateTime = (value: string | null | undefined): string => {
  if (!value) return '—';
  try {
    const utc = value.endsWith('Z') ? value : `${value}Z`;
    return formatInTimeZone(parseISO(utc), 'America/Los_Angeles', 'MMM d, yyyy h:mm a');
  } catch {
    return value;
  }
};

/** Coerce a raw `days` query param to a valid window, falling back to default. */
const parseWindow = (raw: string | null): WindowDays => {
  const n = Number(raw);
  return (WINDOW_OPTIONS as readonly number[]).includes(n) ? (n as WindowDays) : DEFAULT_WINDOW;
};

// ---- Verdict pill styling (verbatim backend verdict; never recomputed) ----
export const verdictClass = (verdict: string | null | undefined): string => {
  const v = (verdict ?? '').toLowerCase();
  if (v === 'confirmed') return 'bg-green-100 text-green-700';
  if (v === 'rejected') return 'bg-red-100 text-red-700';
  // feat-174 — `uncertain` is its own state, never lumped into rejected. Indigo
  // reads as "needs review", distinct from red (rejected) / amber (error) / green
  // (confirmed). No semantic status token exists for this hue (design tokens cover
  // success/warning/danger/preview only; `reading` indigo is reserved off-status),
  // so this mirrors verdictClass's raw-palette style — flagged to design-lead.
  if (v === 'uncertain') return 'bg-indigo-100 text-indigo-700';
  if (v === 'error') return 'bg-amber-100 text-amber-700';
  return 'bg-gray-100 text-gray-600';
};

// feat-160 — reason chips for the server-classified disagreement pair(s). Rendered
// verbatim from `item.disagreements`; renders nothing when there are none. The ONE
// shared chip component — used both here (per-type row) and in the standalone
// disagreements table, so this styling is the single source for both. feat-185 did
// NOT restyle it for exactly that reason.
//
// Design Type-B ①: a bordered rect + leading ⚠ icon on the `warning` token, so the
// chip is unmistakably NOT a verdict pill. The `error` verdict pill is a fill-only
// `rounded-full` amber badge; making the disagreement chip a `rounded` (rect) +
// `border` + icon keeps them visually orthogonal even within the amber family
// (verdict = pill color; disagreement = bordered warn chip). Never re-inline raw
// amber — the `warning` token exists for exactly this.
//
// feat-174 — the `offline_uncertain` token is a REVIEW FLAG, not a conflict, so it
// gets its own treatment: indigo bordered chip + a ? (HelpCircle) icon, deliberately
// distinct from the amber ⚠ conflict chips. (No semantic indigo status token exists
// — see verdictClass note — so raw indigo mirrors the uncertain verdict pill;
// flagged to design-lead for a blessing.)
const NEEDS_REVIEW_TOKENS = new Set(['offline_uncertain']);
export const DisagreementChips: React.FC<{
  tokens: string[] | null | undefined;
  correctHumanVerified: boolean | null | undefined;
}> = ({ tokens, correctHumanVerified }) => {
  if (!tokens || tokens.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {tokens.map((t) => {
        const needsReview = NEEDS_REVIEW_TOKENS.has(t);
        const Icon = needsReview ? HelpCircle : AlertTriangle;
        const chipClass = needsReview
          ? 'border-indigo-300 bg-indigo-50 text-indigo-700'
          : 'border-warning-on/40 bg-warning-surface text-warning-on';
        return (
          <span
            key={t}
            className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[11px] font-semibold ${chipClass}`}
          >
            <Icon className="w-3 h-3 shrink-0" aria-hidden="true" />
            {disagreementChipLabel(t, correctHumanVerified)}
          </span>
        );
      })}
    </div>
  );
};

/** A verdict pill, rendered verbatim from the backend verdict string. */
const VerdictPill: React.FC<{ verdict: string | null | undefined; compact?: boolean }> = ({
  verdict,
  compact,
}) => (
  <span
    className={`inline-block rounded-full font-medium ${
      compact ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-0.5 text-[11px]'
    } ${verdictClass(verdict)}`}
  >
    {verdict ?? 'unknown'}
  </span>
);

// D5 — one source row of the evidence rail. The label lives in a FIXED-WIDTH left
// column so the value (chip or grade) of all three rows starts at the same x — the
// at-a-glance three-way comparison the old layout made impossible (AC3). Removing
// the width from one label is exactly what the AC3 control arm does, and it moves
// that row's chip. Don't make this width content-driven.
const SourceRow: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="flex items-center gap-2 min-h-[20px]" data-testid="source-row">
    <span
      data-testid="source-label"
      className="w-[104px] shrink-0 text-[11px] font-semibold text-inkMuted"
    >
      {label}
    </span>
    <span data-testid="source-value" className="flex items-center gap-2 min-w-0">
      {children}
    </span>
  </div>
);

/**
 * The three-source verdict rail: three aligned source rows (Annotator / Live
 * double-check / Offline judge + grades), no reasoning prose, no rubric chips (D9).
 * `judged === false` (drink_promotion, R7) keeps the "not applicable" / "not judged"
 * copy in place of an empty chip.
 *
 * D5 AS AMENDED by Preston (2026-07-24) — this rail is now rendered ONLY in the
 * EXPANDED state, NOT in the collapsed card. D5 originally put the three verdict
 * chips on the collapsed card; Preston reversed that. Rationale, so nobody restores
 * it: this screen exists to VALIDATE the AI verdicts, so a human grade made WITHOUT
 * first seeing the AI's answer is worth more than an agreement-biased one. Hiding the
 * verdict rail until the reviewer deliberately expands keeps the collapsed fast-path
 * grade INDEPENDENT of the AI (anti-anchoring). The collapsed card is now just
 * transcript + disagreement chips + grade buttons; the disagreement chips STAY
 * collapsed (R2) because they flag "this one's worth reading", a different purpose
 * from surfacing an AI verdict to agree with.
 */
const EvidenceRail: React.FC<{ item: InteractionReviewItem; judged: boolean }> = ({
  item,
  judged,
}) => {
  const offline = (item.judgements ?? []).find((j) => j.source === 'offline_judge');
  const live = (item.judgements ?? []).find((j) => j.source === 'live_double_check');
  const grade = item.data?.grade ?? null;

  return (
    <div className="flex flex-col gap-1">
      <SourceRow label="Annotator">
        <span className="text-[13px] text-ink whitespace-nowrap">
          Grade: <span className="font-medium">{grade ?? '—'}</span>
        </span>
      </SourceRow>

      {/* feat-160 D3 — the live double-check is the annotator's OWN real-time
          second pass at annotation time (feat-156), NOT the independent judge, so
          it keeps its own row above the offline judge and the two are never
          conflated. */}
      <SourceRow label="Live double-check">
        {!judged ? (
          <span className="text-[13px] text-inkMuted italic">Not applicable</span>
        ) : live ? (
          <VerdictPill verdict={live.verdict} />
        ) : (
          <span className="text-[13px] text-inkMuted italic">No live double-check recorded</span>
        )}
      </SourceRow>

      <SourceRow label="Offline judge">
        {!judged ? (
          <span className="text-[13px] text-inkMuted italic">Not judged</span>
        ) : offline ? (
          <>
            <VerdictPill verdict={offline.verdict} />
            {offline.grade && (
              <span className="text-[13px] text-ink whitespace-nowrap">
                Grade: <span className="font-medium">{offline.grade}</span>
              </span>
            )}
          </>
        ) : (
          <span className="text-[13px] text-inkMuted italic">Not judged yet</span>
        )}
      </SourceRow>
    </div>
  );
};

/**
 * The transcript the AI scored — BARE TEXT (D4 as amended by Preston): no clock
 * prefix, no `Speaker N:` prefix. One coral rule on the whole block says "this
 * block is the scored span", which post-feat-183 is literally true. Capped at
 * COLLAPSED_TRANSCRIPT_ROWS with an in-place `+K more lines` — never an internal
 * scrollbar.
 */
const TranscriptBlock: React.FC<{ item: InteractionReviewItem }> = ({ item }) => {
  const parts = item.transcript ?? [];
  const [showAll, setShowAll] = useState(false);
  const overflow = parts.length - COLLAPSED_TRANSCRIPT_ROWS;
  const visible = showAll ? parts : parts.slice(0, COLLAPSED_TRANSCRIPT_ROWS);

  return (
    <div className="rounded-lg border border-softBorder bg-cream/30 border-l-2 border-l-coral px-1.5 py-1.5 text-[13px] leading-[18px]">
      {parts.length === 0 ? (
        <div className="text-inkMuted italic px-1.5 py-1">No transcript parts in the window.</div>
      ) : (
        <>
          {visible.map((p, idx) => {
            // feat-161 / feat-183 predicate, kept verbatim. Currently not rendered
            // (RENDER_IN_SPAN_RULE) — one line to restore.
            const inSpan = isInSpan(p.start_time, item.start_time, item.end_time);
            return (
              <div
                key={`${p.start_time}-${idx}`}
                data-testid="transcript-row"
                data-in-span={inSpan ? 'true' : 'false'}
                className={`pl-1.5 pr-1.5 py-px rounded ${
                  RENDER_IN_SPAN_RULE && inSpan ? 'border-l-2 border-coral' : ''
                }`}
              >
                <span className="text-ink">{p.text}</span>
              </div>
            );
          })}
          {overflow > 0 && (
            <button
              type="button"
              onClick={() => setShowAll((s) => !s)}
              className="mt-0.5 px-1.5 text-[11px] text-coral hover:underline"
            >
              {showAll ? 'Show fewer lines' : `+${overflow} more lines`}
            </button>
          )}
        </>
      )}
    </div>
  );
};

/**
 * D3 — the three-way verdict toggle, in the fixed right gutter so every row's
 * `Correct` sits at the same x and a predictable y-step down the page. `compact`
 * is the thin row's inline form (graded OR Undecided), which stays LIVE so a
 * mis-grade is fixed in place without reopening the row.
 *
 * N1 fix (design-lead's spec, Preston-scheduled 2026-07-24): the compact slots are
 * three EQUAL fixed slots (`flex-1 min-w-0`), and selection is signalled by FILL,
 * never by WIDTH. The prior version widened the selected button with `flex-1` and
 * swapped its single-letter label for the full word, so a graded row's `U`/`I`
 * targets jumped x with the verdict (feat-185 N1: `U` at 1200.5 on a Correct row vs
 * 1145 on an Incorrect one). The rule: width is what the click column PROMISES to
 * hold constant, so selection must never change it. Every slot keeps its 1px
 * `border` (box-sizing: border-box, so the border never shifts geometry) and its
 * fixed letter; only the background/border COLOR reflects the current verdict —
 * including `U` filling on an Undecided thin row (status === null in the compact
 * branch, which is only rendered on a thin row, is exactly the dispositioned case).
 *
 * Colors stay raw green-600/red-600 (not the success/danger tokens): this screen's
 * verdict pills are an established raw-palette island (blessed on feat-174), and a
 * token-green toggle beside a raw-green `confirmed` pill puts two greens in one row.
 */
const ClickColumn: React.FC<{
  status: boolean | null;
  busy: boolean;
  compact?: boolean;
  onMark: (status: boolean | null) => void;
}> = ({ status, busy, compact, onMark }) => {
  if (compact) {
    // One shared slot geometry for all three — equal flex basis + always-present
    // border → identical width and left-x on every slot regardless of verdict.
    const slot =
      'flex-1 min-w-0 px-1 py-0.5 rounded-md border text-[11px] font-semibold text-center transition-colors disabled:opacity-50';
    return (
      <div className="w-[124px] shrink-0 flex items-center gap-1" data-testid="click-column-compact">
        <button
          type="button"
          disabled={busy}
          onClick={() => onMark(true)}
          title="Correct (C)"
          aria-pressed={status === true}
          className={`${slot} ${
            status === true
              ? 'bg-green-600 text-white border-transparent'
              : 'border-green-300 text-green-700 hover:bg-green-50'
          }`}
        >
          C
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => onMark(null)}
          title="Undecided (U) — clears the verdict"
          aria-pressed={status === null}
          // N2 (design-lead corollary): fill-as-selection only works when the fill
          // CONTRASTS the row. Correct/Incorrect fill green/red and clear that bar;
          // the neutral Undecided fill (cream-dark, required to stay in-family and
          // NOT read as a verdict) is a near-background tint — ~1.0 contrast on its
          // own row, so the fill alone is invisible as "selected". So the selected-U
          // slot signals by an EDGE instead: a neutral `ring-inkMuted`, which
          // contrasts the row where the fill can't. A ring is a box-shadow — it
          // paints OUTSIDE the layout box, so slot geometry (and N1 / the spread-0
          // gate) is untouched. Faint cream-dark fill kept so it still reads
          // in-family; the ring does the selection work.
          className={`${slot} ${
            status === null
              ? 'bg-cream-dark text-ink border-transparent ring-1 ring-inkMuted'
              : 'border-softBorder text-inkMuted hover:bg-cream'
          }`}
        >
          U
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => onMark(false)}
          title="Incorrect (I)"
          aria-pressed={status === false}
          className={`${slot} ${
            status === false
              ? 'bg-red-600 text-white border-transparent'
              : 'border-red-300 text-red-700 hover:bg-red-50'
          }`}
        >
          I
        </button>
      </div>
    );
  }

  const base =
    'w-full px-2 py-1 rounded-md border text-[13px] font-medium text-left transition-colors disabled:opacity-50';
  return (
    <div className="w-[124px] shrink-0 pt-5 flex flex-col gap-1" data-testid="click-column">
      <button
        type="button"
        disabled={busy}
        onClick={() => onMark(true)}
        className={`${base} ${
          status === true
            ? 'bg-green-600 text-white border-green-600'
            : 'border-green-300 bg-white text-green-700 hover:bg-green-50'
        }`}
      >
        Correct <span className="font-mono text-[10px] opacity-60 float-right leading-5">C</span>
      </button>
      {/* D3 — Undecided writes `null`, which is also the "not yet reviewed" value,
          so it never renders as a SELECTED state: a null row is indistinguishable
          from an ungraded one in the data (the accepted D3 trade-off). Styling it
          selected would assert a distinction the database doesn't hold. */}
      <button
        type="button"
        disabled={busy}
        onClick={() => onMark(null)}
        className={`${base} border-softBorder bg-white text-inkMuted hover:bg-cream`}
      >
        Undecided <span className="font-mono text-[10px] opacity-60 float-right leading-5">U</span>
      </button>
      <button
        type="button"
        disabled={busy}
        onClick={() => onMark(false)}
        className={`${base} ${
          status === false
            ? 'bg-red-600 text-white border-red-600'
            : 'border-red-300 bg-white text-red-700 hover:bg-red-50'
        }`}
      >
        Incorrect <span className="font-mono text-[10px] opacity-60 float-right leading-5">I</span>
      </button>
    </div>
  );
};

/** R3 — one reasoning block in the expanded surface. NO rubric chips (D9). */
const ReasoningBlock: React.FC<{ title: React.ReactNode; text: string | null | undefined }> = ({
  title,
  text,
}) => (
  <div>
    <div className="text-[11px] font-semibold uppercase tracking-wide text-inkMuted mb-1">
      {title}
    </div>
    <p className="text-[13px] leading-[19px] text-inkMuted whitespace-pre-wrap">
      {text || <span className="italic">No reasoning recorded.</span>}
    </p>
  </div>
);

/** The inline write error (R4) — replaces the old `alert()`. */
const InlineWriteError: React.FC<{ message: string }> = ({ message }) => (
  <div
    role="alert"
    data-testid="write-error"
    className="mt-1.5 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-[11px] text-red-700"
  >
    {message}
  </div>
);

/**
 * feat-191 slice (e) — auto-refresh cadence. ADR 0001 §A-7: 90s / K=5, ruled on
 * query shape rather than a stopwatch (one isolate is a primary-key lookup with the
 * window and composition predicates short-circuited and no MD5 filesort; a full
 * re-fetch is not). If a real staging measurement ever puts one isolate above
 * ~150ms, these drop to 2 and 180_000 together.
 */
const SWEEP_INTERVAL_MS = 90_000;
const SWEEP_LOOKAHEAD = 5;

const judgementFor = (item: InteractionReviewItem, source: string): ReviewJudgement | undefined =>
  (item.judgements ?? []).find((j) => j.source === source);

/**
 * One row of the stack. Four renderings:
 *  • graded + not reopened      → the D6 thin row, green/red (live click column)
 *  • Undecided + dispositioned  → the D6 thin row, NEUTRAL (Preston 2026-07-24)
 *  • collapsed (default)        → transcript + evidence rail + click column
 *  • expanded                   → the collapsed row plus the reasoning surface
 */
const StackRow: React.FC<{
  item: InteractionReviewItem;
  judged: boolean;
  busy: boolean;
  focused: boolean;
  expanded: boolean;
  reopened: boolean;
  dispositioned: boolean;
  /**
   * feat-191 (slice e) — another evaluator decided this row while it sat in this
   * session's list. A FOURTH row state, and deliberately distinct from the other
   * two collapsed ones: those are outcomes of YOUR action and are reopenable
   * because you own them; this is an external event that happened to your queue.
   */
  decidedElsewhere: boolean;
  error: string | null;
  onMark: (status: boolean | null) => void;
  onToggleExpand: () => void;
  onReopen: () => void;
  onFocus: () => void;
  rowRef: (el: HTMLElement | null) => void;
}> = ({
  item,
  judged,
  busy,
  focused,
  expanded,
  reopened,
  dispositioned,
  decidedElsewhere,
  error,
  onMark,
  onToggleExpand,
  onReopen,
  onFocus,
  rowRef,
}) => {
  const status = item.correct_human_verified;
  const graded = status != null && !reopened;
  // D3 (Preston 2026-07-24) — a session-dispositioned Undecided row (status null).
  // Reopening it clears the disposition, so `dispositioned` already implies "not
  // reopened"; no extra guard needed.
  const undecided = status == null && dispositioned;
  const thin = graded || undecided;

  // D6 — the thin row. Its snippet IS its identity now (D4 amended: no span, no
  // clocks, no speaker labels here either). Green Correct and red Incorrect are
  // SETTLED verdicts → filled pills + a colored rail. Undecided is the ABSENCE of a
  // verdict (per design-lead: a collapsed Undecided row and an ungraded card both
  // render `null`, so Undecided must stay reopen-obvious and must NOT read as
  // settled). So it gets neither a filled pill nor a colored rail: an OUTLINE
  // inkMuted "Undecided" chip that mirrors the Undecided toggle's own treatment, and
  // a near-absent softBorder rail. Height + the outline chip mark it as a distinct
  // "skipped" row without asserting a decision the data doesn't hold.
  // feat-191 slice (e) — STATE 4: decided by another evaluator (design-lead ruling).
  // The axis is AGENCY, not verdict: this row is not yours to act on. So the signal
  // is structural, not chromatic —
  //   • the click gutter keeps its width and holds NO control. feat-185's N1 rule is
  //     that selection changes fill, never geometry, and actionability lives in that
  //     column; an empty one IS "you can't act on this", and the constant width is
  //     what keeps the row from moving when it changes state.
  //   • de-emphasis is `text-inkMuted`, NOT `opacity-*` — opacity compounds with
  //     already-muted text and silently drops it under 4.5:1.
  //   • the copy is unattributed. Part E is admin-only precisely because it names
  //     graders; naming one here would leak sideways the thing that panel is gated
  //     to protect. So: no email, no verified_at.
  //   • the other person's VERDICT is not shown. It would read as one you hold, and
  //     on a surface where the evaluator's independent judgement is the product and
  //     their agreement-with-judge is measured, adjacent verdicts are an anchoring
  //     path. The safe option is also the simpler one.
  // No reopen: reopening would invite re-grading something already decided, which is
  // the exact thing this refresh exists to prevent.
  if (decidedElsewhere) {
    const snippet = (item.transcript ?? []).map((p) => p.text).join(' ');
    return (
      <section
        ref={rowRef}
        data-testid="review-row"
        data-graded="none"
        data-disposition="decided-elsewhere"
        data-focused={focused ? 'true' : 'false'}
        onMouseDown={onFocus}
        className={`flex items-center gap-3 px-3 py-1.5 border-l-4 ${
          focused ? 'border-ink bg-cream-dark' : 'border-softBorder bg-cream-dark/40'
        }`}
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold shrink-0 border border-softBorder bg-white text-inkMuted whitespace-nowrap">
            Decided by another evaluator
          </span>
          <span className="text-[13px] text-inkMuted truncate min-w-0">{snippet}</span>
        </div>
        {/* Same width as the live click column, deliberately empty. */}
        <div className="w-[124px] shrink-0" data-testid="click-column-empty" aria-hidden="true" />
      </section>
    );
  }

  if (thin) {
    const snippet = (item.transcript ?? []).map((p) => p.text).join(' ');
    const offline = judgementFor(item, 'offline_judge');
    const live = judgementFor(item, 'live_double_check');
    const railClass = focused
      ? 'border-ink bg-cream-dark'
      : status === true
      ? 'border-green-600 bg-green-50/40'
      : status === false
      ? 'border-red-600 bg-red-50/40'
      : 'border-softBorder bg-cream-dark/40';
    return (
      <section
        ref={rowRef}
        data-testid="review-row"
        data-graded={status === true ? 'correct' : status === false ? 'incorrect' : 'none'}
        data-disposition={undecided ? 'undecided' : 'none'}
        data-focused={focused ? 'true' : 'false'}
        onMouseDown={onFocus}
        className={`flex items-center gap-3 px-3 py-1.5 border-l-4 ${railClass}`}
      >
        <button
          type="button"
          onClick={onReopen}
          className="flex items-center gap-3 flex-1 min-w-0 text-left"
        >
          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold shrink-0 ${
              status === true
                ? 'bg-green-600 text-white'
                : status === false
                ? 'bg-red-600 text-white'
                : 'border border-softBorder bg-white text-inkMuted'
            }`}
          >
            {status === true ? '✓ Correct' : status === false ? '✕ Incorrect' : 'Undecided'}
          </span>
          <span className="text-[13px] text-ink truncate min-w-0">{snippet}</span>
          {judged && (
            <span className="hidden lg:inline-flex items-center gap-1 shrink-0">
              {live && <VerdictPill verdict={live.verdict} compact />}
              {offline && <VerdictPill verdict={offline.verdict} compact />}
            </span>
          )}
          {(item.verified_by || item.verified_at) && (
            <span className="text-[11px] text-inkMuted shrink-0 hidden xl:inline">
              {item.verified_by || '—'}
              {item.verified_at ? ` · ${formatDateTime(item.verified_at)}` : ''}
            </span>
          )}
          <span className="text-[11px] text-coral shrink-0">Reopen ⌄</span>
        </button>
        <ClickColumn status={status} busy={busy} compact onMark={onMark} />
        {error && (
          <div className="w-full">
            <InlineWriteError message={error} />
          </div>
        )}
      </section>
    );
  }

  return (
    <section
      ref={rowRef}
      data-testid="review-row"
      data-graded="none"
      data-disposition="none"
      data-focused={focused ? 'true' : 'false'}
      onMouseDown={onFocus}
      className={`flex flex-col border-l-4 ${
        focused ? 'border-ink bg-cream-dark' : expanded ? 'border-transparent bg-cream/20' : 'border-transparent'
      }`}
    >
      <div className="flex gap-3 px-3 py-2.5">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-1">
            {/* D4 — View shift is the ONLY header affordance left. No name, no
                location, no date-time, no span. */}
            {item.server_id && (
              <Link
                to={`/restaurant/shifts/${item.shift_id}/transcript/${item.server_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[11px] text-coral hover:underline whitespace-nowrap"
                title="Open the full shift transcript in a new tab"
              >
                View shift ↗
              </Link>
            )}
            {/* feat-160 DisagreementChips moved to the expander (Preston 2026-07-24,
                ruling (a)). A "Judge rejected" / "Judge uncertain" chip leaks the
                offline judge's verdict + direction — the exact anchor the D5
                amendment removes — so it can't stay on the collapsed fast path. The
                collapsed header now holds only View shift + the AI evidence toggle;
                the toggle's `ml-auto` keeps it right-aligned with no orphaned gap.
                Accepted tradeoff: loses R2's collapsed "which rows to read" signal in
                favour of a fully un-anchored grade. */}
            <button
              type="button"
              onClick={onToggleExpand}
              aria-expanded={expanded}
              className={`text-[11px] ml-auto whitespace-nowrap ${
                expanded ? 'text-ink font-medium' : 'text-inkMuted hover:text-ink'
              }`}
            >
              {/* D5 amended — the expander now reveals the three VERDICTS + grades as
                  well as the prose, so "Reasoning" undersells it. "AI evidence" is
                  neutral (doesn't scream "the answer's in here", preserving the
                  anti-anchoring intent) and matches the domain vocab (the rail was the
                  "evidence rail"). design-lead call, 2026-07-24. */}
              {expanded ? 'AI evidence ⌃' : 'AI evidence ⌄'}
            </button>
          </div>
          <TranscriptBlock item={item} />
          {error && <InlineWriteError message={error} />}
        </div>

        {/* D5 amended (Preston 2026-07-24) — the verdict rail moved to the expanded
            block below. Collapsed card is transcript + grade buttons only, so the
            human grade is made without first seeing the AI's answer (anti-anchoring). */}
        <ClickColumn status={status} busy={busy} onMark={onMark} />
      </div>

      {expanded && (
        <div
          data-testid="expanded-reasoning"
          className="px-3 pb-3 flex flex-col gap-3 border-t border-softBorder pt-3"
        >
          {/* feat-186 — the item's server-authoritative queue bucket, rendered
              VERBATIM from `item.bucket` (never re-derived from tokens, D-B). Lives
              in the expander (not the collapsed fast path) on purpose: the bucket
              summarizes the AI-agreement state (Agreement/Discrepancy/Uncertain), so
              surfacing it collapsed would re-anchor the grade — the exact thing
              feat-185's D5 amendment moved the verdict rail here to avoid. */}
          {isReviewBucket(item.bucket) && (
            <div data-testid="bucket-chip" className="flex items-center gap-1.5 text-[11px]">
              <span
                className={`inline-block w-2 h-2 rounded-full ${BUCKET_SEGMENT_CLASS[item.bucket]}`}
              />
              <span className="text-inkMuted">
                Queue bucket:{' '}
                <span className="font-semibold text-ink">{BUCKET_LABEL[item.bucket]}</span>
              </span>
            </div>
          )}
          {/* feat-160 DisagreementChips lead the expander (Preston ruling (a),
              2026-07-24) — they summarize what's contested, so they sit above the
              verdict rail. Renders nothing when there are no tokens (no empty gap).
              Unchanged component — still the single shared chip used by the
              standalone disagreements table; only its LOCATION moved. */}
          <DisagreementChips
            tokens={item.disagreements}
            correctHumanVerified={item.correct_human_verified}
          />
          {/* D5 amended — the three-source verdict rail leads the reasoning:
              verdicts are the summary, the prose below is the detail. */}
          <div data-testid="expanded-verdict-rail">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-inkMuted mb-1">
              AI verdicts
            </div>
            <EvidenceRail item={item} judged={judged} />
          </div>
          <ReasoningBlock title="Annotator (AI) — reasoning" text={item.data?.reasoning ?? null} />
          {judged ? (
            <>
              <ReasoningBlock
                title={
                  <>
                    Live double-check{' '}
                    <span className="font-normal normal-case">(real-time, at annotation)</span> —
                    reasoning
                  </>
                }
                text={judgementFor(item, 'live_double_check')?.reasoning ?? null}
              />
              <ReasoningBlock
                title="Offline judge — reasoning"
                text={judgementFor(item, 'offline_judge')?.reasoning ?? null}
              />
            </>
          ) : (
            <div className="text-[13px] text-inkMuted italic">
              Not judged (human review only) — no live double-check or offline judge reasoning.
            </div>
          )}
          {/* spec §3 — the span survives HERE only. Preston stripped it from the
              header and the transcript rows (the fast path); expanded is the
              "I need to investigate this one" surface, and this is the only thing
              left that locates the interaction in time before you follow
              `View shift ↗` into a 90-minute transcript. */}
          <div className="text-[11px] text-inkMuted border-t border-softBorder pt-2">
            <span className="font-mono text-ink">
              span {formatClock(item.start_time)}–
              {item.end_time != null ? formatClock(item.end_time) : '…'}
            </span>
            <br />
            Human verification:{' '}
            {status === true && <span className="text-green-600 font-medium">Correct</span>}
            {status === false && <span className="text-red-600 font-medium">Incorrect</span>}
            {status == null && <span className="text-ink font-medium">Not yet verified</span>}
            {status != null && (item.verified_by || item.verified_at) && (
              <span>
                {' '}· by {item.verified_by || '—'}
                {item.verified_at ? ` · ${formatDateTime(item.verified_at)}` : ''}
              </span>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

// ============================================================================
// feat-186 — the queue-composition ("Mix") control. Variant A (Preston-approved,
// `design/queue-composition-control.html`): a compact toolbar chip showing a live
// mini stacked-bar + "70/25/5", which opens a popover with the preset row (the
// PRIMARY action — this control inherits the prioritization job the retired "Only
// disagreements" filter did), the draggable 2-divider proportion bar (secondary
// fine-tune), and the agreement max-days-back control. Bucket hues reuse the card
// semantics (amber Discrepancy / indigo Uncertain / green Agreement).
// ============================================================================

/** Stacked segments for a set of shares. Zero-width buckets collapse to nothing
 * (mockup state 2 — no orphaned sliver / 0% clutter). */
const MixSegments: React.FC<{ shares: BucketShares }> = ({ shares }) => (
  <>
    {REVIEW_BUCKETS.map((b) =>
      shares[b] > 0 ? (
        <span
          key={b}
          data-bucket={b}
          className={`h-full ${BUCKET_SEGMENT_CLASS[b]}`}
          style={{ width: `${shares[b]}%` }}
        />
      ) : null,
    )}
  </>
);

/**
 * feat-188 (R1) — the "exclude already-reviewed" control. Design-lead ruled its
 * placement as FIRST in the popover's lower SCOPE section (above "Spot-check
 * agreements within"): the popover's top block answers WHAT PROPORTIONS (presets +
 * mix bar, which stay the primary action), the lower block answers WHICH
 * POPULATION, and this is a population gate. Scope controls run broad → narrow, and
 * this one spans all three buckets while the spot-check cap touches Agreement only.
 *
 * Extracted as a component because it renders in TWO places: inside MixControl's
 * popover for judged types, and standalone in the toolbar for drink_promotion —
 * which has no popover at all (MixControl is judged-only) yet still honors the
 * filter (ADR D-2 / AC9). Without the standalone render the filter would be ON
 * there with no way to switch it off, and D5 promises exactly that escape hatch.
 *
 * The sub-explainer is LOAD-BEARING, not decoration (design-lead was explicit): D2
 * makes NULL mean both "never reviewed" and "Undecided", so an item the reviewer
 * Cleared reappears with the filter ON. Unexplained that reads as a bug ("I cleared
 * it — why is it back?"); with the line it reads as intended.
 */
const ExcludeReviewedCheckbox: React.FC<{
  checked: boolean;
  onChange: (on: boolean) => void;
  /**
   * `popover` — a scope row inside MixControl, stacked label-over-explainer.
   * `toolbar` — drink_promotion's standalone render. Design-lead's refinement: with
   * no popover to sit in, a popover-shaped row reads as a floating orphan, so it
   * takes the toolbar-toggle idiom of the neighbouring "Only current prompt
   * template" control — checkbox and label inline on one line. The COPY is identical
   * in both; only the chrome changes.
   */
  variant?: 'popover' | 'toolbar';
}> = ({ checked, onChange, variant = 'popover' }) => {
  const input = (
    <input
      type="checkbox"
      data-testid="exclude-reviewed-checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      className={`h-4 w-4 rounded border-softBorder text-coral focus:ring-coral ${
        variant === 'popover' ? 'mt-0.5' : ''
      }`}
    />
  );
  if (variant === 'toolbar') {
    return (
      <div className="flex flex-col">
        <label className="flex items-center gap-2 text-sm font-medium text-ink cursor-pointer">
          {input}
          Hide interactions already reviewed
        </label>
        {/* VISIBLE, not a tooltip — design-lead was explicit, and the reason is that
            the reviewer about to hit the misread has no cause to hover looking for
            the answer; they need it before the confusion, not behind it.
            Design-lead pre-authorised shortening this variant to the load-bearing
            CLAUSE if the full two-sentence line reads cramped beside the template
            toggle, and it does: at ~70 characters it would be the widest item in a
            horizontal toolbar and would force the row to wrap. "Reviewed = marked
            Correct or Incorrect" is clarifying; "Undecided items stay in the queue"
            is the part that stops "I cleared it — why is it back?" from reading as a
            bug. The popover variant, which owns a full-width column, keeps both. */}
        <span className="text-[11px] text-inkMuted ml-6">
          Undecided items stay in the queue.
        </span>
      </div>
    );
  }
  return (
    <div>
      <label className="flex items-start gap-2 cursor-pointer">
        {input}
        <span className="text-sm font-medium text-ink">Hide interactions already reviewed</span>
      </label>
      <p className="text-[11px] text-inkMuted mt-1 ml-6">
        Reviewed = marked Correct or Incorrect. Undecided items stay in the queue.
      </p>
    </div>
  );
};

const MixControl: React.FC<{
  shares: BucketShares;
  agreementMaxDays: number;
  windowDays: WindowDays;
  excludeHumanReviewed: boolean;
  onCommitShares: (s: BucketShares) => void;
  onChangeMaxDays: (d: number) => void;
  onChangeExcludeHumanReviewed: (on: boolean) => void;
}> = ({
  shares,
  agreementMaxDays,
  windowDays,
  excludeHumanReviewed,
  onCommitShares,
  onChangeMaxDays,
  onChangeExcludeHumanReviewed,
}) => {
  const [open, setOpen] = useState(false);
  // `draft` is the live (possibly mid-drag) tune; it commits to the URL-backed
  // `shares` only on drag-release / preset / keyboard-blur, so a drag doesn't
  // refetch on every pixel. `draftRef` mirrors it for the window pointer handlers.
  const [draft, setDraft] = useState<BucketShares>(shares);
  const draftRef = useRef<BucketShares>(shares);
  const barRef = useRef<HTMLDivElement | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const draggingRef = useRef<0 | 1 | null>(null);

  const setDraftBoth = useCallback((s: BucketShares) => {
    draftRef.current = s;
    setDraft(s);
  }, []);

  // Sync the draft when the committed shares change from OUTSIDE (URL nav, a
  // preset applied elsewhere) — but never yank the bar out from under a live drag.
  useEffect(() => {
    if (draggingRef.current == null) setDraftBoth(shares);
  }, [shares, setDraftBoth]);

  // Commit a tune to the URL — normalized, and only when it actually differs, so
  // a no-op blur/close doesn't trigger a redundant refetch.
  const commit = useCallback(
    (s: BucketShares) => {
      const n = normalizeShares(s);
      if (
        n.discrepancy === shares.discrepancy &&
        n.uncertain === shares.uncertain &&
        n.agreement === shares.agreement
      ) {
        return;
      }
      onCommitShares(n);
    },
    [shares, onCommitShares],
  );

  const close = useCallback(() => {
    setOpen(false);
    commit(draftRef.current); // flush any uncommitted fine-tune
  }, [commit]);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  // --- drag: two dividers on a 100-wide bar. p1 = D|U boundary, p2 = U|A. ---
  const applyPointer = useCallback(
    (clientX: number) => {
      const el = barRef.current;
      const handle = draggingRef.current;
      if (!el || handle == null) return;
      const rect = el.getBoundingClientRect();
      if (rect.width <= 0) return;
      const pct = Math.max(0, Math.min(100, Math.round(((clientX - rect.left) / rect.width) * 100)));
      const d = draftRef.current;
      const b1 = d.discrepancy;
      const b2 = d.discrepancy + d.uncertain;
      const next =
        handle === 0
          ? (() => {
              const a = Math.min(pct, b2);
              return { discrepancy: a, uncertain: b2 - a, agreement: 100 - b2 };
            })()
          : (() => {
              const b = Math.max(pct, b1);
              return { discrepancy: b1, uncertain: b - b1, agreement: 100 - b };
            })();
      setDraftBoth(next);
    },
    [setDraftBoth],
  );

  const startDrag = (handle: 0 | 1) => (e: React.PointerEvent) => {
    e.preventDefault();
    draggingRef.current = handle;
    const move = (ev: PointerEvent) => applyPointer(ev.clientX);
    const up = () => {
      draggingRef.current = null;
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      commit(draftRef.current);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  // --- keyboard fine-tune (flag 3: presets are the keyboard path, but the handles
  // still need an AT-reachable nudge + focus/aria). Arrow ±1, Shift+Arrow ±5;
  // commits on Enter / blur (not per-keystroke, to avoid a refetch storm). ---
  const nudge = (handle: 0 | 1, delta: number) => {
    const d = draftRef.current;
    const b1 = d.discrepancy;
    const b2 = d.discrepancy + d.uncertain;
    const next =
      handle === 0
        ? (() => {
            const a = Math.max(0, Math.min(b2, b1 + delta));
            return { discrepancy: a, uncertain: b2 - a, agreement: 100 - b2 };
          })()
        : (() => {
            const b = Math.max(b1, Math.min(100, b2 + delta));
            return { discrepancy: b1, uncertain: b - b1, agreement: 100 - b };
          })();
    setDraftBoth(next);
  };
  const onHandleKey = (handle: 0 | 1) => (e: React.KeyboardEvent) => {
    const step = e.shiftKey ? 5 : 1;
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      nudge(handle, -step);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      nudge(handle, step);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      commit(draftRef.current);
    }
  };

  const applyPreset = (p: CompositionPreset) => {
    setDraftBoth(p.shares);
    commit(p.shares);
  };

  const p1 = draft.discrepancy;
  const p2 = draft.discrepancy + draft.uncertain;
  const activePreset = matchPreset(shares);
  const maxDaysOptions = agreementMaxDaysOptions(windowDays);

  const handle = (which: 0 | 1, leftPct: number, valuenow: number, label: string) => (
    <div className="absolute top-0 bottom-0" style={{ left: `${leftPct}%` }}>
      <div className="w-1 h-full bg-white/70 -ml-0.5 pointer-events-none" />
      <div
        role="slider"
        tabIndex={0}
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={valuenow}
        aria-valuetext={`${valuenow}%`}
        onPointerDown={startDrag(which)}
        onKeyDown={onHandleKey(which)}
        onBlur={() => commit(draftRef.current)}
        className="absolute -top-1 left-1/2 -translate-x-1/2 w-4 h-4 rounded-full bg-white ring-2 ring-ink shadow cursor-ew-resize focus:outline-none focus:ring-coral"
      />
    </div>
  );

  return (
    <div className="flex items-center gap-2" ref={wrapRef}>
      <span className="text-sm font-medium text-ink">Mix:</span>
      <div className="relative">
        <button
          type="button"
          data-testid="mix-chip"
          onClick={() => (open ? close() : setOpen(true))}
          aria-expanded={open}
          aria-haspopup="dialog"
          title="Batch composition — Discrepancy / Uncertain / Agreement"
          className="inline-flex items-center gap-2 rounded-lg border border-softBorder bg-white pl-1.5 pr-2 py-1 hover:bg-cream"
        >
          <span className="inline-flex h-3.5 w-24 rounded-sm overflow-hidden ring-1 ring-softBorder">
            <MixSegments shares={shares} />
          </span>
          <span className="font-mono text-xs text-ink" data-testid="mix-label">
            {`${shares.discrepancy}/${shares.uncertain}/${shares.agreement}`}
          </span>
          <span className="text-inkMuted text-[10px]" aria-hidden="true">
            ▾
          </span>
        </button>

        {open && (
          <div
            role="dialog"
            aria-label="Batch composition"
            data-testid="mix-popover"
            className="absolute left-0 top-full mt-2 z-40 w-[380px] max-w-[calc(100vw-2rem)] rounded-xl border border-softBorder bg-white shadow-lg p-4"
          >
            <div className="text-sm font-semibold text-ink mb-0.5">Batch composition</div>
            <div className="text-[11px] text-inkMuted mb-2">
              Start with a preset — the everyday move is “mostly discrepancies”. Fine-tune below only
              if you need to.
            </div>

            {/* presets = the PRIMARY action (this replaces the old "Only disagreements" filter) */}
            <div className="flex flex-wrap gap-1.5 mb-3">
              {COMPOSITION_PRESETS.map((p) => {
                const selected = activePreset?.id === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    data-testid={`mix-preset-${p.id}`}
                    aria-pressed={selected}
                    onClick={() => applyPreset(p)}
                    className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                      selected
                        ? 'bg-coral text-white'
                        : 'border border-softBorder text-ink hover:bg-cream'
                    }`}
                  >
                    {p.label} · {p.shares.discrepancy}/{p.shares.uncertain}/{p.shares.agreement}
                  </button>
                );
              })}
              {!activePreset && (
                <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-cream-dark text-ink">
                  Custom
                </span>
              )}
            </div>

            {/* the draggable proportion bar (fine-tune) */}
            <div
              ref={barRef}
              data-testid="mix-bar"
              className="relative h-8 rounded-lg overflow-hidden ring-1 ring-softBorder mb-1 select-none touch-none"
            >
              <div className="flex h-full">
                {REVIEW_BUCKETS.map((b) =>
                  draft[b] > 0 ? (
                    <div
                      key={b}
                      className={`h-full flex items-center justify-center ${BUCKET_SEGMENT_CLASS[b]}`}
                      style={{ width: `${draft[b]}%` }}
                    >
                      {draft[b] >= 8 && (
                        <span className="text-white text-xs font-bold">{draft[b]}%</span>
                      )}
                    </div>
                  ) : null,
                )}
              </div>
              {handle(0, p1, p1, 'Discrepancy / Uncertain boundary')}
              {handle(1, p2, p2, 'Uncertain / Agreement boundary')}
            </div>

            <div className="flex items-center justify-between text-[11px] mb-3">
              {REVIEW_BUCKETS.map((b) => (
                <span key={b} className="inline-flex items-center gap-1 text-inkMuted">
                  <span className={`inline-block w-2 h-2 rounded-full ${BUCKET_SEGMENT_CLASS[b]}`} />
                  {BUCKET_LABEL[b]} {draft[b]}%
                </span>
              ))}
            </div>

            {/* feat-188 (R1) — the SCOPE section: which population, as opposed to the
                proportion controls above. Ordered broad → narrow per design-lead:
                exclude-reviewed (all three buckets) then the spot-check cap
                (Agreement only). The section divider moved onto this wrapper so the
                two scope controls read as one group rather than two stacked blocks. */}
            <div className="border-t border-softBorder pt-3">
              <ExcludeReviewedCheckbox
                checked={excludeHumanReviewed}
                onChange={onChangeExcludeHumanReviewed}
              />

              {/* agreement max-days-back — scopes only the Agreement spot-check */}
              <div className="flex items-center justify-between gap-2 mt-3">
                <label className="text-sm font-medium text-ink">Spot-check agreements within</label>
                <div className="inline-flex rounded-lg border border-softBorder bg-white overflow-hidden text-sm">
                  {maxDaysOptions.map((d) => (
                    <button
                      key={d}
                      type="button"
                      data-testid={`mix-maxdays-${d}`}
                      onClick={() => onChangeMaxDays(d)}
                      className={`px-2 py-1 ${
                        agreementMaxDays === d ? 'bg-coral text-white' : 'text-inkMuted hover:bg-cream'
                      }`}
                    >
                      {d}d
                    </button>
                  ))}
                </div>
              </div>
              <p className="text-[11px] text-inkMuted mt-1">
                Agreement spot-checks are a random sample; never older than this. Default = the review
                Window ({windowDays}d) so you don’t spot-check stale detections. Discrepancy /
                Uncertain always honor the Window.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const InteractionTypeReview: React.FC = () => {
  const { interactionType = '' } = useParams<{ interactionType: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();

  const validType = isReviewType(interactionType);
  const judged = isJudgedType(interactionType);

  const windowDays = parseWindow(searchParams.get('days'));
  const locationId = searchParams.get('location_id') || '';

  // feat-186 — queue-composition state, all URL-encoded (shareable/bookmarkable,
  // D7). Gated on `judged`: drink_promotion is never judged, so composition is
  // inert there and we never send the params — a stray `?shares=` can't blank its
  // list (AC8). This REPLACES the removed binary "Only disagreements" filter (AC6);
  // its capability is the 100/0/0 preset (AC2).
  const shares = useMemo<BucketShares>(
    () =>
      judged
        ? normalizeShares(parseSharesParam(searchParams.get('shares')) ?? DEFAULT_SHARES)
        : DEFAULT_SHARES,
    [judged, searchParams],
  );
  // Agreement spot-check cap (D-E). Default = the review Window; honored only for
  // the Agreement bucket. A malformed/≤0 value falls back to the Window.
  const agreementMaxDaysBack = useMemo(() => {
    const n = Number(searchParams.get('agreement_days'));
    return Number.isFinite(n) && n >= 1 ? Math.floor(n) : windowDays;
  }, [searchParams, windowDays]);
  // D5 — only shifts under a currently-active annotation prompt version. Off by default.
  const onlyCurrentTemplate = judged && searchParams.get('only_current_template') === 'true';
  // feat-188 (ADR 0001 D-5) — exclude interactions a human has already DECIDED.
  // Default **ON**, and default-ON is expressed by ABSENCE: only the opt-out is
  // written to the URL, as the canonical literal `exclude_human_reviewed=false`.
  // Any other literal resolves to ON, so a hand-mangled URL fails SAFE toward the
  // un-reviewed queue rather than 422-ing the page (AC7/AC8).
  //
  // NOT gated on `judged`, unlike the composition params above: this is a POPULATION
  // filter and it applies to drink_promotion too (ADR D-2 / AC9) — that feed is never
  // judged, but it IS human-reviewed through the same feat-131 PUT.
  //
  // LOAD-BEARING (feat-188 D2): "reviewed" means DECIDED — Correct or Incorrect.
  // `correct_human_verified` is NULL for BOTH "never reviewed" AND an explicit
  // "Undecided", and the two are indistinguishable in the schema (no tri-state
  // column; a migration is out of scope). So an Undecided item deliberately STAYS in
  // the queue — it still needs adjudication. Do NOT "fix" this into also hiding
  // Undecided without a schema change.
  const excludeHumanReviewed = searchParams.get('exclude_human_reviewed') !== 'false';
  // feat-196 (AC10) — "Live clients only", carried in from the dashboard/disagreements
  // link. Read with the IDENTICAL rule as `exclude_human_reviewed` directly above,
  // deliberately: default-ON expressed by ABSENCE, `=false` the one canonical opt-out,
  // any other literal resolving to ON so a hand-mangled URL fails SAFE rather than
  // 422-ing the page. Two default-ON URL filters on one screen that parsed differently
  // would be the drift; ADR 0001 §4.3 makes this the contract.
  //
  // NOT gated on `judged` (same reason as the line above): it is a POPULATION filter
  // and applies to drink_promotion too.
  const liveOnly = searchParams.get('live_only') !== 'false';
  // D7 — the agreement sample seed. FE generates it once on first load (below) and
  // keeps it in the URL so a shared link reproduces the EXACT spot-check sample.
  const agreementSeed = searchParams.get('seed') || '';
  // feat-183 — single-interaction isolate. `?interaction_id=<shift_interaction.id>`
  // pulls up EXACTLY that one interaction, independent of the days/disagreements
  // window (the backend short-circuits to `WHERE si.id = %s`). Distinct from
  // `?focus=` (which deep-links WITHIN the current filters). In this mode we hide
  // the window/location/disagreements controls and the paging — there is one row.
  const singleId = searchParams.get('interaction_id') || '';
  const singleMode = validType && !!singleId;

  const [locations, setLocations] = useState<LocationOption[]>([]);
  const [items, setItems] = useState<InteractionReviewItem[]>([]);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());
  const [reopenedIds, setReopenedIds] = useState<Set<string>>(() => new Set());
  // D3 (Preston 2026-07-24) — rows the reviewer pressed `U` (Undecided) on THIS
  // SESSION. Undecided writes `null`, which is ALSO the "not yet reviewed" value, so
  // a null row can't collapse via the persisted-status path without faking a
  // verdict. This session-local set is what lets an Undecided row collapse to its
  // thin row and be skipped by the advance scan — exactly like a graded row, but
  // WITHOUT pretending the backend holds a decision it doesn't. It is client state
  // only: it does not persist, and on refresh these rows return to the open state
  // (Undecided durability is the D3 reopen, a separate decision pending Preston).
  const [dispositionedIds, setDispositionedIds] = useState<Set<string>>(() => new Set());
  /**
   * feat-191 slice (e) — rows another evaluator decided while they sat in THIS
   * session's list. Session-local and keyed by id, exactly like dispositionedIds.
   *
   * MARK, never remove (ADR 0001 A-6 as amended). The original design removed
   * decided rows ahead of the cursor and argued position was preserved because the
   * removals sat past it — sound for a flashcard, where one card is on screen, and
   * wrong here: this is a LIST, rows a few ahead of the focus are usually visible,
   * and deleting them out from under a reader is the same "cards vanish" symptom
   * that design was written to avoid. Marking instead makes `items` APPEND-ONLY
   * outside load(), which is a stronger guarantee than index arithmetic: nothing
   * before, at, or after the cursor moves, so there is no position bookkeeping to
   * get wrong.
   */
  const [decidedElsewhereIds, setDecidedElsewhereIds] = useState<Set<string>>(() => new Set());
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [loadMoreFailed, setLoadMoreFailed] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingId, setPendingId] = useState<string | null>(null);

  const rowEls = useRef<Record<string, HTMLElement | null>>({});

  // feat-186 (§D-J cursor) — the three-stream next-offsets to send on "load more".
  // A ref (not state) so loadMore reads the latest without re-binding; reset to
  // {0,0,0} on every fresh filter and advanced from the response `next_*` fields.
  const nextOffsetsRef = useRef<{ d: number; u: number; a: number }>({ d: 0, u: 0, a: 0 });

  /**
   * feat-188 — rows SERVED by the single-stream (drink_promotion) feed, which is not
   * the same as rows RENDERED once load-more dedupes by id.
   *
   * Judged feeds page by server cursor (`next_d/u/a_offset` off the response), so
   * dropping a duplicate on append can't desynchronise them. drink_promotion has no
   * cursor — the envelope carries only limit/offset/count/has_more — so its next
   * offset used to be derived from `items.length`. The moment dedupe drops a row,
   * that array stops counting what the server has served and every later page asks
   * for an offset one short, permanently: a fully-duplicate page re-requests the
   * SAME offset (paging stalls, and the sentinel doesn't re-fire because its deps
   * include `items.length`, which didn't change), and a partly-duplicate page lags
   * by the number dropped, compounding.
   *
   * Found by QA with a two-test repro against the committed dedupe. The fix is to
   * keep the two quantities separate rather than to weaken the dedupe: position in
   * the server's stream is counted from what the SERVER returned (raw page length),
   * never from the filtered array.
   */
  const servedCountRef = useRef(0);

  /**
   * feat-188 — pages successfully fetched. STATE, not a ref, and its only job is to
   * be a dependency of the auto-load effect below.
   *
   * `items.length` used to double as the "a page landed" signal: every successful
   * page changed it, so the IntersectionObserver effect re-ran, disconnected the old
   * observer and created a fresh one — which fires immediately if the sentinel is
   * still in view. Dedupe severed that. A page consumed ENTIRELY as duplicates leaves
   * `items.length` unchanged, so the effect doesn't re-run, and IntersectionObserver
   * only emits on an intersection CHANGE: the sentinel was already intersecting and
   * still is, so nothing fires again. Auto-load would stop silently, with no visible
   * affordance — the manual button is the FAILURE fallback and nothing failed.
   *
   * Same root cause as the drink_promotion offset bug (rendered ≠ served), surfacing
   * in the sentinel instead of the cursor. Architect flagged it; this counter is the
   * re-subscription signal that no longer depends on anything rendering.
   */
  const [pagesLoaded, setPagesLoaded] = useState(0);

  // Location options for the filter (loaded once). Non-fatal — filter just stays
  // "All locations" on failure.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await getInteractionEvalLocations({ liveOnly });
        if (!cancelled) setLocations(rows as LocationOption[]);
      } catch (e) {
        Sentry.captureException(e);
      }
    })();
    return () => {
      cancelled = true;
    };
    // feat-196 (D3) — the dropdown must not OFFER a non-live client's location, so
    // the option list is scoped by the toggle rather than loaded once.
  }, [liveOnly]);

  // D7 — generate the agreement seed once for a judged feed and write it into the
  // URL, so the agreement sample is reproducible from a shared/bookmarked link.
  // load() waits for this to land (below), so the FIRST judged fetch already
  // carries the seed — no throwaway "0"-sample fetch then reload.
  useEffect(() => {
    if (!judged || singleMode) return;
    if (searchParams.get('seed')) return;
    const next = new URLSearchParams(searchParams);
    next.set('seed', generateAgreementSeed());
    setSearchParams(next, { replace: true });
  }, [judged, singleMode, searchParams, setSearchParams]);

  const load = useCallback(async () => {
    if (!validType) {
      setIsLoading(false);
      return;
    }
    // Hold the first judged fetch until the seed effect has written the URL seed,
    // so the agreement sample is reproducible from load one.
    if (judged && !singleMode && !agreementSeed) return;
    setIsLoading(true);
    setError(null);
    // Fresh filter → paging restarts from the top of all three streams, and from the
    // top of the single stream (both cursors reset together or the next load-more
    // pages from a position that belongs to the previous filter).
    nextOffsetsRef.current = { d: 0, u: 0, a: 0 };
    servedCountRef.current = 0;
    setPagesLoaded(0);
    try {
      const resp = await getInteractionReviewList(
        // feat-183 — single-interaction isolate: send only interaction_id (backend
        // ignores the window here).
        singleMode
          ? { interactionType, interactionId: singleId, limit: 1 }
          : judged
          ? {
              // feat-186 — judged feed composed to the requested 3-bucket mix.
              interactionType,
              days: windowDays,
              locationId: locationId || undefined,
              limit: PAGE_SIZE,
              shares,
              agreementMaxDaysBack,
              agreementSeed: agreementSeed || undefined,
              onlyCurrentPromptTemplate: onlyCurrentTemplate,
              excludeHumanReviewed,
              liveOnly,
              dOffset: 0,
              uOffset: 0,
              aOffset: 0,
            }
          : {
              // drink_promotion — never judged, no composition (AC8): plain
              // newest-first single-stream list, exactly as before.
              interactionType,
              days: windowDays,
              locationId: locationId || undefined,
              limit: PAGE_SIZE,
              offset: 0,
              // feat-188 (AC9) — the ONE param that is NOT judged-only. This feed has
              // no buckets, but its rows are human-reviewed, so the filter is fully
              // meaningful here. Omitting it would make AC9 silently a no-op.
              excludeHumanReviewed,
              liveOnly,
            },
      );
      setItems(resp.items);
      setFocusedIndex(0); // reset the focus position on a fresh filter
      setExpandedIds(new Set());
      setReopenedIds(new Set());
      setDispositionedIds(new Set());
      setDecidedElsewhereIds(new Set());
      setRowErrors({});
      setLoadMoreFailed(false);
      // §D-J — advance the cursor + honor the explicit has_more (the old
      // count>=PAGE_SIZE heuristic is broken by graceful degrade). Fall back to the
      // heuristic only if the backend omitted has_more (pre-deploy tolerance).
      nextOffsetsRef.current = {
        d: resp.next_d_offset ?? 0,
        u: resp.next_u_offset ?? 0,
        a: resp.next_a_offset ?? 0,
      };
      // The single-stream cursor advances by what the SERVER returned. On the first
      // page that equals the rendered count (nothing to dedupe against), but it is
      // set from the response so the two can never diverge in origin.
      servedCountRef.current = resp.items.length;
      setPagesLoaded(1);
      setHasMore(!singleMode && (resp.has_more ?? resp.count >= PAGE_SIZE));
    } catch (e) {
      console.error('Failed to load interaction review list:', e);
      Sentry.captureException(e);
      setError(e instanceof Error ? e.message : 'Failed to load data');
    } finally {
      setIsLoading(false);
    }
  }, [
    validType,
    interactionType,
    windowDays,
    locationId,
    singleMode,
    singleId,
    judged,
    shares,
    agreementMaxDaysBack,
    agreementSeed,
    onlyCurrentTemplate,
    // feat-188 D-5 — toggling the filter changes the eligible POPULATION, so the
    // fetch must re-run and paging must restart from the top of every stream
    // (load() resets nextOffsetsRef on entry). The agreement seed is deliberately
    // NOT regenerated: MD5(CONCAT(id, seed)) is a total order that does not depend
    // on the filter, so the un-reviewed subset keeps the same relative order and
    // feat-186 AC4 stability holds over the reduced pool (ADR D-3).
    excludeHumanReviewed,
    liveOnly,
  ]);

  useEffect(() => {
    load();
  }, [load]);

  // feat-160 (ADR 0001 D4b) — deep-link from the standalone disagreements view:
  // `?focus=<id>` FOCUSES that interaction's row (and pages forward to find it).
  // One-shot, initialized once at mount so it never hijacks later J/K navigation.
  const focusRef = useRef<string | null>(searchParams.get('focus'));

  // Fetch + append the next page; returns how many rows were added. For judged
  // feeds this pages the three streams independently via the §D-J cursor (send the
  // current d/u/a offsets, receive the next ones) — that is what keeps Discrepancy/
  // Uncertain exhaustive-no-gaps and Agreement non-duplicating (AC3/AC4). For
  // drink_promotion it keeps the single-offset append.
  const loadMore = useCallback(async (): Promise<number> => {
    setIsLoadingMore(true);
    try {
      const resp = await getInteractionReviewList(
        judged
          ? {
              interactionType,
              days: windowDays,
              locationId: locationId || undefined,
              limit: PAGE_SIZE,
              shares,
              agreementMaxDaysBack,
              agreementSeed: agreementSeed || undefined,
              onlyCurrentPromptTemplate: onlyCurrentTemplate,
              // R3 — the filter rides load-more too, or page 2 would re-admit the
              // already-reviewed rows page 1 excluded.
              excludeHumanReviewed,
              liveOnly,
              dOffset: nextOffsetsRef.current.d,
              uOffset: nextOffsetsRef.current.u,
              aOffset: nextOffsetsRef.current.a,
            }
          : {
              interactionType,
              days: windowDays,
              locationId: locationId || undefined,
              limit: PAGE_SIZE,
              // NOT `items.length` — that counts rows RENDERED, and dedupe makes it
              // lag behind rows SERVED. See servedCountRef.
              offset: servedCountRef.current,
              excludeHumanReviewed,
              liveOnly,
            },
      );
      // R6 / AC9 — APPEND only. Already-rendered rows keep their identity, their
      // graded state and their scroll position; the sentinel below has a reserved
      // fixed height so nothing shifts when page 2 lands.
      //
      // feat-188 — append DEDUPED BY ID. ADR D-4 says offset paging over a set the
      // filter is shrinking SKIPS rows and never duplicates them, and it forbids
      // compensating the offsets (over-correcting turns a recoverable skip into
      // duplicate cards with colliding React keys). This is not that compensation —
      // offsets are untouched — it is the guard that makes the forbidden failure mode
      // unreachable from the other end.
      //
      // The direction D-4 doesn't cover is a row RE-ENTERING the population, which
      // shifts later rows UP rather than down and CAN re-serve something already
      // appended. It is reachable: Undecided persists `null` (see the disposition
      // write below), and `null` is exactly what the filter treats as un-reviewed —
      // so Reopen → Undecided on a row graded earlier in the session puts it back in
      // the server's eligible set. Backend (feat-188 slice) mutation-proved the server
      // half of this: NULL present → Correct absent → Reopen(NULL) present again.
      // Two reviewers on one window reach it without any reopen at all.
      //
      // Deduping costs nothing when there is nothing to dedupe, and the alternative
      // failure is duplicate React keys, which corrupts row identity rather than
      // merely showing a row twice.
      //
      // Both cursors advance from the RESPONSE, never from this filtered array: the
      // judged streams from `next_*`, the single stream from `servedCountRef` (raw
      // page length). An earlier version of this change derived the single-stream
      // offset from `items.length` and stalled drink_promotion paging the moment a
      // row was dropped — see servedCountRef for the mechanism.
      setItems((prev) => {
        const seen = new Set(prev.map((i) => i.id));
        return [...prev, ...resp.items.filter((i) => !seen.has(i.id))];
      });
      nextOffsetsRef.current = {
        d: resp.next_d_offset ?? nextOffsetsRef.current.d,
        u: resp.next_u_offset ?? nextOffsetsRef.current.u,
        a: resp.next_a_offset ?? nextOffsetsRef.current.a,
      };
      // Advance by the RAW page length, before dedupe. A page consumed entirely as
      // duplicates still moves the single-stream cursor forward, so paging continues
      // instead of re-requesting the same offset forever.
      servedCountRef.current += resp.items.length;
      // Counted whether or not anything rendered — that is the whole point.
      setPagesLoaded((n) => n + 1);
      setHasMore(resp.has_more ?? resp.count >= PAGE_SIZE);
      setLoadMoreFailed(false);
      return resp.items.length;
    } catch (e) {
      Sentry.captureException(e);
      // spec §3 — a failed fetch degrades the auto-load sentinel into a real
      // `Load more` button rather than stalling silently.
      setLoadMoreFailed(true);
      return 0;
    } finally {
      setIsLoadingMore(false);
    }
  }, [
    interactionType,
    windowDays,
    locationId,
    judged,
    shares,
    agreementMaxDaysBack,
    agreementSeed,
    onlyCurrentTemplate,
    excludeHumanReviewed,
    liveOnly,
    // `items.length` is deliberately NOT here any more: loadMore no longer reads it
    // (the single-stream offset comes from servedCountRef), and leaving a dependency
    // the callback doesn't use is what eslint flags — it also re-created this
    // callback on every append for no reason. The auto-load effect reads loadMore
    // through `loadMoreRef`, and the focus effect already depends on `items`, so
    // nothing needed the identity churn.
  ]);

  const loadMoreRef = useRef(loadMore);
  loadMoreRef.current = loadMore;
  const busyRef = useRef(false);
  busyRef.current = isLoadingMore;

  // R6 — auto-load on scroll. An always-on button would re-introduce the `Next →`
  // hard stop this feature exists to delete, so the button is the FAILURE fallback
  // only. jsdom (and any environment without IntersectionObserver) falls back to
  // the visible button, so paging is never unreachable.
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const canAutoLoad = typeof IntersectionObserver !== 'undefined';
  useEffect(() => {
    if (singleMode || !hasMore || loadMoreFailed || !canAutoLoad) return;
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting) && !busyRef.current) {
          loadMoreRef.current();
        }
      },
      { rootMargin: '400px' },
    );
    io.observe(el);
    return () => io.disconnect();
    // `pagesLoaded` sits alongside `items.length` rather than replacing it: the
    // rendered count is what first mounts the sentinel (the effect can't attach
    // before it exists), and the page counter is what keeps the subscription alive
    // across pages that render nothing. Dropping either one reopens a gap.
  }, [singleMode, hasMore, loadMoreFailed, canAutoLoad, items.length, pagesLoaded]);

  // feat-160 (ADR 0001 D4b) — land on the focused interaction: if it's in the
  // loaded set, focus it; otherwise page forward until found or the feed is
  // exhausted, then give up and stay at index 0 (the item may be outside the
  // window — no hard error).
  useEffect(() => {
    // feat-183 — no focus/paging in single-interaction isolate mode (one row).
    if (singleMode) return;
    // Wait for ANY in-flight fetch to settle before deciding — otherwise the
    // initial empty state, or the moment loadMore flips isLoadingMore, would trip
    // the give-up branch before the next page arrives.
    if (!focusRef.current || isLoading || isLoadingMore) return;
    const idx = items.findIndex((i) => i.id === focusRef.current);
    if (idx >= 0) {
      setFocusedIndex(idx);
      focusRef.current = null;
    } else if (hasMore && !loadMoreFailed) {
      loadMore();
    } else {
      focusRef.current = null; // no more pages (or empty feed) — fall back to index 0
    }
  }, [items, hasMore, isLoadingMore, isLoading, loadMore, singleMode, loadMoreFailed]);

  // R5 — the focused row is scrolled into view. `block: 'nearest'` never re-anchors
  // the viewport to the top of the list (R3), so expanding or grading a row leaves
  // the reviewer where they were. Guarded for jsdom.
  const focusedId = items[focusedIndex]?.id;
  // feat-191 slice (e) — the sweep reads these through refs so its interval is
  // bound once per filter rather than re-created on every J/K press (which would
  // reset the 90s timer and, with fast navigation, mean it never fires).
  const focusedIndexRef = useRef(focusedIndex);
  focusedIndexRef.current = focusedIndex;
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const decidedElsewhereRef = useRef(decidedElsewhereIds);
  decidedElsewhereRef.current = decidedElsewhereIds;
  useEffect(() => {
    if (!focusedId) return;
    const el = rowEls.current[focusedId];
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'nearest', behavior: 'auto' });
    }
  }, [focusedId]);


  /**
   * feat-191 slice (e) — the id-isolate probe. ADR 0001 §A-6.
   *
   * Asks about ONE card by primary key (`?interaction_id=<uuid>`) rather than
   * re-fetching the queue. Re-fetching page 1 and dropping what it no longer
   * contains is wrong beyond page 1: the judged feed is a three-stream composition
   * with independent cursors and a seeded-MD5 sample, and this component auto-loads
   * pages, so a card at local index 40 is LEGITIMATELY absent from a fresh page of
   * 25 and its absence says nothing about whether anyone graded it. The isolate
   * path short-circuits to `WHERE si.id = %s`, and feat-188's exclude filter is
   * INERT there by design, so a decided row comes back WITH its status instead of
   * 404-ing — which is the entire read this design rests on.
   *
   * A failed probe is a NO-OP. Never mark a card because a request errored: an
   * evaluator losing a card to a network blip is worse than a stale one. Failures
   * are deliberately not sent to Sentry either — this runs on a timer, so a flaky
   * connection would flood it with events nobody will action.
   */
  const myEmail = (user?.managementUser?.email || user?.email || '').toLowerCase();
  const probeInFlight = useRef<Set<string>>(new Set());

  const probeDecided = useCallback(
    async (id: string) => {
      if (!validType || !id || probeInFlight.current.has(id)) return;
      probeInFlight.current.add(id);
      try {
        const row = await getInteractionReviewById(interactionType, id);
        if (!row) return;
        // Decided AND by somebody else. `verified_by` is the caller's email, so a
        // row this evaluator graded a moment ago must NOT be marked "decided by
        // another evaluator" — that would misattribute their own work back to them,
        // and Part E measures their throughput by exactly this column.
        const decidedByOther =
          row.correct_human_verified != null &&
          !!row.verified_by &&
          row.verified_by.toLowerCase() !== myEmail;
        if (!decidedByOther) return;
        setDecidedElsewhereIds((prev) => {
          if (prev.has(id)) return prev;
          const next = new Set(prev);
          next.add(id);
          return next;
        });
      } catch {
        // No-op by design. See above.
      } finally {
        probeInFlight.current.delete(id);
      }
    },
    [validType, interactionType, myEmail],
  );

  /**
   * D-a — advance-time verification. The correctness guarantee: a card's status is
   * checked as the evaluator arrives at it, BEFORE they can grade it. Lookahead 1
   * warms the next card's probe while the current one is displayed, so by the time
   * they press J the answer has usually already landed.
   *
   * Triggered by `focusedIndex` moving (J/K, arrows, click) — in this component
   * "advancing to a card" is a focus change, not a card swap.
   */
  useEffect(() => {
    if (singleMode || isLoading) return;
    const focused = items[focusedIndex];
    const next = items[focusedIndex + 1];
    [focused, next].forEach((row) => {
      if (!row) return;
      // Never probe the row being written right now, one already known decided, or
      // one already marked.
      if (pendingId === row.id) return;
      if (row.correct_human_verified != null) return;
      if (decidedElsewhereIds.has(row.id)) return;
      probeDecided(row.id);
    });
    // `decidedElsewhereIds` is deliberately NOT a dependency: it is written by this
    // effect, and depending on it would re-run the effect on every mark.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusedIndex, items, singleMode, isLoading, pendingId, probeDecided]);

  /**
   * D-b — the background lookahead sweep. The freshness guarantee, and what AC12
   * is actually asking for: probe the next K not-yet-viewed cards ahead of the
   * focus on a timer, so a card decided elsewhere is already marked by the time the
   * evaluator reaches it.
   *
   * 90s and K=5 are the architect's ruling (A-7), on query shape rather than a
   * stopwatch: one isolate is a primary-key lookup with every window and
   * composition predicate short-circuited and no MD5 filesort, so it is cheaper
   * than a full re-fetch by a category rather than by a constant.
   *
   * Skips the tick entirely while loading, paging, mid-write, or backgrounded.
   */
  useEffect(() => {
    if (singleMode) return;
    const tick = () => {
      if (isLoading || isLoadingMore || pendingId != null) return;
      if (typeof document !== 'undefined' && document.hidden) return;
      const start = focusedIndexRef.current + 1;
      let probed = 0;
      for (let i = start; i < itemsRef.current.length && probed < SWEEP_LOOKAHEAD; i += 1) {
        const row = itemsRef.current[i];
        if (!row) continue;
        if (row.correct_human_verified != null) continue;
        if (decidedElsewhereRef.current.has(row.id)) continue;
        probeDecided(row.id);
        probed += 1;
      }
    };
    const handle = setInterval(tick, SWEEP_INTERVAL_MS);
    return () => clearInterval(handle);
  }, [singleMode, isLoading, isLoadingMore, pendingId, probeDecided]);

  // Update the URL (shareable + preserves selectors) when window/location change.
  const setWindow = (d: WindowDays) => {
    const next = new URLSearchParams(searchParams);
    next.set('days', String(d));
    setSearchParams(next, { replace: true });
  };
  const setLocation = (id: string) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set('location_id', id);
    else next.delete('location_id');
    setSearchParams(next, { replace: true });
  };
  // feat-186 — the composition control writes its state into the URL (D7). Changing
  // the mix / cap / template restarts paging from the top of the streams, which
  // load()'s fresh-filter reset handles (nextOffsets → 0). The seed is left as-is,
  // so retuning shares keeps the SAME agreement sample.
  const setShares = (s: BucketShares) => {
    const next = new URLSearchParams(searchParams);
    next.set('shares', sharesToParam(s));
    setSearchParams(next, { replace: true });
  };
  const setAgreementMaxDays = (d: number) => {
    const next = new URLSearchParams(searchParams);
    next.set('agreement_days', String(d));
    setSearchParams(next, { replace: true });
  };
  const setOnlyCurrentTemplate = (on: boolean) => {
    const next = new URLSearchParams(searchParams);
    if (on) next.set('only_current_template', 'true');
    else next.delete('only_current_template');
    setSearchParams(next, { replace: true });
  };
  // feat-188 (ADR 0001 D-5) — ON is the default, so turning the filter back ON
  // DELETES the key rather than writing `=true`. That keeps exactly one canonical
  // URL per state: a bare URL and a toggled-ON URL are byte-identical, so a shared
  // link can never encode "ON" ambiguously (AC7). Changing the filter changes the
  // eligible population, so paging must restart — `load()` resets nextOffsetsRef to
  // {0,0,0} whenever a filter dep changes, and this flag is in its dep list.
  const setExcludeHumanReviewed = (on: boolean) => {
    const next = new URLSearchParams(searchParams);
    if (on) next.delete('exclude_human_reviewed');
    else next.set('exclude_human_reviewed', 'false');
    setSearchParams(next, { replace: true });
  };

  // feat-196 — same canonical-URL discipline: ON deletes the key, OFF writes the
  // single literal `live_only=false`. Changing it changes the eligible population,
  // so paging restarts via load()'s fresh-filter reset (the flag is in its deps).
  const setLiveOnly = (on: boolean) => {
    const next = new URLSearchParams(searchParams);
    if (on) next.delete('live_only');
    else next.set('live_only', 'false');
    setSearchParams(next, { replace: true });
  };

  // R4 — Correct / Undecided / Incorrect reuse the feat-131 verify write.
  // Optimistic: reflect status + verified_by/at (the endpoint stamps caller email +
  // now UTC), rolling back THAT ONE ROW on failure with an inline error (never an
  // alert(), never a silent success). Backend returns {status:"success"} only, so we
  // mirror its attribution locally rather than re-fetch.
  const writeCorrectness = useCallback(
    async (item: InteractionReviewItem, status: boolean | null): Promise<boolean> => {
      const previous = items.find((i) => i.id === item.id) ?? item;
      setPendingId(item.id);
      setRowErrors((prev) => {
        if (!(item.id in prev)) return prev;
        const next = { ...prev };
        delete next[item.id];
        return next;
      });
      setItems((prev) =>
        prev.map((i) =>
          i.id === item.id
            ? {
                ...i,
                correct_human_verified: status,
                verified_by: status === null ? null : user?.email ?? i.verified_by ?? null,
                verified_at: status === null ? null : new Date().toISOString(),
              }
            : i,
        ),
      );
      const fail = (message: string) => {
        // Roll back ONLY this row (AC6) — other rows keep their state, including
        // any write that landed while this one was in flight.
        setItems((prev) => prev.map((i) => (i.id === item.id ? previous : i)));
        setRowErrors((prev) => ({ ...prev, [item.id]: message }));
      };
      try {
        const response = await apiCall(`/shift-interactions/${item.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ correct_human_verified: status }),
        });
        if (!response.ok) {
          if (response.status === 401) {
            fail('Your session has expired. Please sign in again to grade interactions.');
          } else if (response.status === 403) {
            fail(
              'You are not authorized to grade interactions. Only Bespoke admins can verify interaction correctness.',
            );
          } else {
            fail('Failed to update interaction.');
          }
          return false;
        }
        return true;
      } catch (err) {
        console.error('Error updating interaction:', err);
        Sentry.captureException(err);
        fail('Failed to update interaction.');
        return false;
      } finally {
        setPendingId(null);
      }
    },
    [items, user],
  );

  /**
   * R4 — disposition the row at `index`. On a successful write ALL THREE of
   * Correct / Incorrect / Undecided behave the same (Preston 2026-07-24, reversing
   * the earlier "Undecided is quiet" choice): the row collapses to its thin row and
   * focus advances to the next UNDECIDED row.
   *
   * The wrinkle is that Undecided writes `null`, which is ALSO "not yet reviewed",
   * so it can't collapse via the persisted-status path. C/I collapse because their
   * status is non-null; U collapses because we record a SESSION-LOCAL disposition in
   * `dispositionedIds`. C/I clear any prior disposition (a real grade supersedes it);
   * U adds one. The advance scan then skips rows that are graded OR dispositioned.
   */
  const markAt = useCallback(
    async (index: number, status: boolean | null) => {
      const item = items[index];
      if (!item) return;
      const ok = await writeCorrectness(item, status);
      if (!ok) return;
      // Collapse: drop any expand + reopen override for this row.
      setExpandedIds((prev) => {
        if (!prev.has(item.id)) return prev;
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
      setReopenedIds((prev) => {
        if (!prev.has(item.id)) return prev;
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
      // U adds a session disposition (its null write can't collapse on its own);
      // C/I remove any prior one (their persisted status is the real decision now).
      setDispositionedIds((prev) => {
        const has = prev.has(item.id);
        if (status === null ? has : !has) return prev; // no-op fast path
        const next = new Set(prev);
        if (status === null) next.add(item.id);
        else next.delete(item.id);
        return next;
      });
      // Advance to the next row that is neither graded NOR dispositioned. `items` is
      // the pre-write snapshot and `dispositionedIds` is the closure value, but the
      // only row either disagrees with is `index` itself, which `idx > index` skips.
      const nextOpen = items.findIndex(
        (i, idx) =>
          idx > index && i.correct_human_verified == null && !dispositionedIds.has(i.id),
      );
      if (nextOpen >= 0) setFocusedIndex(nextOpen);
    },
    [items, writeCorrectness, dispositionedIds],
  );

  const toggleExpand = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Reopen a thin row back to the open card. A session-dispositioned (Undecided)
  // row reopens simply by DROPPING its disposition — that returns it to the default
  // open state and clears the "undecided" mark. A persisted graded row reopens via
  // the reopen override (its status stays on the backend until re-graded). The two
  // are mutually exclusive (markAt keeps a row in at most one set), so this can't
  // double-handle.
  const reopen = useCallback(
    (id: string) => {
      if (dispositionedIds.has(id)) {
        setDispositionedIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
        return;
      }
      setReopenedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    },
    [dispositionedIds],
  );

  // R5 — keyboard flow. A ref keeps the handler fresh without re-binding the
  // listener each render.
  const kb = useRef({
    count: 0,
    focusedIndex: 0,
    focusedId: undefined as string | undefined,
    busy: false,
    markAt,
    toggleExpand,
    reopen,
    thin: false,
  });
  // `thin` = the focused row is currently collapsed to a thin row — a persisted
  // grade not reopened, OR a session-dispositioned Undecided row. Enter/Space then
  // REOPENS it (there is no reasoning to expand on a thin row); an open row expands.
  const focusedStatus = items[focusedIndex]?.correct_human_verified;
  kb.current = {
    count: items.length,
    focusedIndex,
    focusedId,
    busy: pendingId != null,
    markAt,
    toggleExpand,
    reopen,
    thin:
      focusedId != null &&
      ((focusedStatus != null && !reopenedIds.has(focusedId)) ||
        (focusedStatus == null && dispositionedIds.has(focusedId))),
  };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      const tag = t?.tagName;
      // R5 — never fire while a form control has focus (the location <select> is
      // right there in the sticky toolbar).
      if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA' || t?.isContentEditable) return;
      const { count, focusedIndex: idx, focusedId: id, busy } = kb.current;
      const key = e.key;
      if (key === 'j' || key === 'J' || key === 'ArrowDown') {
        e.preventDefault();
        setFocusedIndex((i) => Math.min(i + 1, Math.max(count - 1, 0)));
      } else if (key === 'k' || key === 'K' || key === 'ArrowUp') {
        e.preventDefault();
        setFocusedIndex((i) => Math.max(i - 1, 0));
      } else if ((key === 'c' || key === 'C') && count > 0 && !busy) {
        kb.current.markAt(idx, true);
      } else if ((key === 'i' || key === 'I') && count > 0 && !busy) {
        kb.current.markAt(idx, false);
      } else if ((key === 'u' || key === 'U') && count > 0 && !busy) {
        kb.current.markAt(idx, null);
      } else if (key === 'Enter' || key === ' ') {
        // Buttons and links activate natively on Enter/Space — don't double-fire.
        if (tag === 'BUTTON' || tag === 'A' || !id) return;
        e.preventDefault();
        // A thin row's (graded or Undecided) expand affordance is "reopen"; an open
        // row's is the reasoning expander.
        if (kb.current.thin) kb.current.reopen(id);
        else kb.current.toggleExpand(id);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const reviewedCount = useMemo(
    () => items.filter((i) => i.correct_human_verified != null).length,
    [items],
  );
  /**
   * feat-188 (R1) — the progress pill is FILTER-CONDITIONAL in BOTH its verb and its
   * count, because the two states measure genuinely different quantities.
   *
   *   • filter OFF — "N of M reviewed", counting PERSISTED decisions
   *     (`correct_human_verified != null`). A status readout over a mixed set; N can
   *     be > 0 on load. Unchanged from today, and the feat-185 D3 constraint still
   *     governs here: a session-only Undecided must not inflate a number that
   *     otherwise survives a refresh.
   *   • filter ON  — "N of M cleared", counting HANDLED: any row the reviewer
   *     dispositioned this session, INCLUDING Undecided (`dispositionedIds`), on top
   *     of anything already persisted. A session BURNDOWN through a worklist.
   *
   * Ruled by PRESTON 2026-07-25 (relayed by product-lead, recorded in spec.md
   * "Reviewed progress pill — SETTLED"), settling the HANDLED-vs-DECIDED fork that
   * design-lead opened and the architect first ruled, then retracted. Marking a row
   * Undecided IS working the card: R4 collapses it to a thin row and the advance scan
   * skips it, so a DECIDED count would leave a reviewer staring at "0 of 25 cleared"
   * above 25 collapsed rows — the broken-looking state the pill exists to prevent.
   *
   * Why the count may differ by state without being incoherent: the fetch filter asks
   * "is there still work to do on this row NEXT fetch?" (persistent state — an
   * Undecided row is still un-adjudicated and is deliberately re-served, D2), while
   * the ON-state pill asks "did I deal with this card THIS session?". Different
   * questions, different answers, no contradiction.
   *
   * Known and accepted: `dispositionedIds` is session state that `load()` clears, so
   * the ON-state number resets on refresh. That is not the asymmetry it looks like —
   * with the filter ON, `reviewedCount` resets too, BY CONSTRUCTION: the next fetch
   * returns only `correct_human_verified IS NULL` rows, so this session's Correct /
   * Incorrect writes are excluded from the new batch and the count returns to 0
   * either way. The stability argument that motivates DECIDED is real for the OFF
   * state and moot for the ON state — which is why the count is split by state
   * rather than picked globally.
   */
  const handledCount = useMemo(
    () =>
      items.filter((i) => i.correct_human_verified != null || dispositionedIds.has(i.id)).length,
    [items, dispositionedIds],
  );
  const progressCount = excludeHumanReviewed ? handledCount : reviewedCount;
  // The "all done" banner tracks the SAME number as the pill. Under the ON state that
  // is what lets it fire at all: gated on persisted decisions it could never appear
  // once a single row was marked Undecided.
  const allReviewed = items.length > 0 && progressCount === items.length && !hasMore;
  const progressPct = items.length === 0 ? 0 : Math.round((progressCount / items.length) * 100);

  // feat-186 (D-B / D8) — the ACHIEVED composition of the loaded rows, counted from
  // the server-authoritative `bucket` (never re-derived from tokens). Feeds the
  // "In view" caption + the graceful-degrade note. A bucket whose achieved share
  // fell ≥5 pts below its requested share, with another bucket overrunning to fill
  // it (D8 fill order D→U→A), is flagged as degraded.
  const achieved = useMemo(() => {
    if (!judged || singleMode) return null;
    const counts: BucketShares = { discrepancy: 0, uncertain: 0, agreement: 0 };
    let tagged = 0;
    for (const it of items) {
      if (isReviewBucket(it.bucket)) {
        counts[it.bucket] += 1;
        tagged += 1;
      }
    }
    if (tagged === 0) return null;
    const achievedShares = normalizeShares(counts);
    let short: ReviewBucket | null = null;
    let shortDeficit = 0;
    let from: ReviewBucket | null = null;
    let fromSurplus = 0;
    for (const b of REVIEW_BUCKETS) {
      const deficit = shares[b] - achievedShares[b];
      if (deficit > shortDeficit) {
        short = b;
        shortDeficit = deficit;
      }
      const surplus = achievedShares[b] - shares[b];
      if (surplus > fromSurplus) {
        from = b;
        fromSurplus = surplus;
      }
    }
    const degraded =
      short && from && shortDeficit >= 5
        ? { short, from, filledPct: Math.round(shortDeficit) }
        : undefined;
    return { shares: achievedShares, degraded };
  }, [items, judged, singleMode, shares]);

  return (
    <div className="min-h-screen bg-cream">
      <ScorecardNavigation />

      <div className="max-w-6xl mx-auto px-4 md:px-8 pt-4 pb-8">
        {/* AC1 — the page preamble is ONE line. It used to be a 4xl h1 plus two
            description paragraphs (~164px from the nav to the toolbar), which put
            the top of the stack at y=335 and left only TWO rows fully visible at
            scroll 0. AC1 says "without scrolling", so that was a real fail — the
            scrolled frame everyone (me included) was measuring hid it.
            The request's own rule decides what goes: nothing that isn't
            load-bearing for the fast path is rendered by default, and two
            paragraphs explaining the screen on a density screen are exactly that.
            What SURVIVES, deliberately:
              • the Back link — the only way back up the drill-down;
              • the feat-160 "scoped to judged interactions" caveat — it explains
                why these counts differ from the parent dashboard, so dropping it
                would create a real misreading (compacted, not deleted);
              • the title, shrunk to text-lg — a reviewer already on this page
                knows what screen they're on, and the toolbar badge repeats it.
            The pixels came from HERE, never from the transcript: rows stay 20px
            and all 8 lines stay visible. That is the feature. */}
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-3">
          <Link
            to="/restaurant/internal/interaction-evaluations"
            className="text-sm text-coral hover:underline whitespace-nowrap"
          >
            ← Back to Interaction Evaluations
          </Link>
          <h1 className="text-lg font-display font-semibold text-ink">
            Review: {prettyType(interactionType)}
          </h1>
          {judged && (
            <span className="text-xs text-inkMuted">
              Scoped to interactions the offline judge has evaluated — un-judged detections are hidden.
            </span>
          )}
        </div>

        {!validType ? (
          <div className="p-6 bg-white border border-softBorder rounded-xl text-sm text-inkMuted">
            Review is not available for <span className="font-medium">{prettyType(interactionType)}</span>{' '}
            yet. Only Initial Greeting, Server Upsell, and Drink Promotion can be reviewed one type at a time.
          </div>
        ) : (
          <>
            {/* feat-183 — single-interaction isolate banner. In this mode the
                days/location/disagreements controls are hidden (the id short-circuits
                the window server-side); offer a one-click escape back to the full
                filtered feed for this type. */}
            {singleMode && (
              <div className="mb-4 flex flex-wrap items-center justify-between gap-2 p-3 rounded-lg border border-coral/30 bg-coral/5 text-sm text-ink">
                <span>
                  Showing a <span className="font-medium">single isolated interaction</span> — filters
                  (window, location, disagreements) don't apply here.
                </span>
                <Link
                  to={`/restaurant/internal/interaction-evaluations/review/${interactionType}`}
                  className="text-coral hover:underline whitespace-nowrap font-medium"
                >
                  View all {prettyType(interactionType).toLowerCase()} →
                </Link>
              </div>
            )}

            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                Error loading data: {error}
              </div>
            )}

            {/* R1 — the sticky toolbar: filters + progress, visible while the stack
                scrolls. It pins BELOW the global ScorecardNavigation (`sticky top-0
                z-50`); at top-0 the whole bar slides UNDER the nav and disappears.
                OFFSET IS `top-24` (96px), NOT the spec's `top-[62px]`: 62px came
                from design-lead's simplified mockup nav, but the REAL nav measured
                85.5px @1280px and feat-151 (30ef3ef) deliberately bumped this
                family from top-[72px] to top-24 to clear it with ~10px of margin
                for its height variance (welcome-row badges flex-wrap, tenant-name
                length). 62px would re-tuck the bar 23px under the nav — the exact
                incident feat-151 fixed. Under-clearance is a functional break;
                over-clearance costs a few px of scroll, so this biases high.
                Matches the PilotTrends sticky precedent (`top-24 z-30`). */}
            <div className="sticky top-24 z-30 -mx-4 md:-mx-8 px-4 md:px-8 py-2 mb-4 bg-cream/95 backdrop-blur border-b border-softBorder">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                <span
                  data-testid="review-type-badge"
                  className="px-2.5 py-1 rounded-full bg-coral text-white text-xs font-semibold whitespace-nowrap"
                >
                  {prettyType(interactionType)}
                </span>
                {/* spec §3 — `N of M+`. The endpoint returns no total (hasMore is
                    just count >= PAGE_SIZE), so a true M would need a backend count
                    this feature excludes. Never fake a denominator. */}
                <div className="flex items-center gap-2">
                  <span
                    data-testid="review-progress-pill"
                    className="text-sm text-ink font-medium whitespace-nowrap"
                  >
                    {singleMode
                      ? 'Isolated interaction'
                      : // feat-188 — a filter-ON window with nothing left to do is a
                        // legitimately empty page, not a failure (AC8). "0 of 0
                        // cleared" reads like a stalled counter, so say the true
                        // thing instead.
                      excludeHumanReviewed && items.length === 0 && !isLoading
                      ? 'All caught up'
                      : `${progressCount} of ${items.length}${hasMore ? '+' : ''} ${
                          excludeHumanReviewed ? 'cleared' : 'reviewed'
                        }`}
                  </span>
                  {!singleMode && items.length > 0 && (
                    <div className="h-1.5 w-16 rounded-full bg-cream-dark overflow-hidden">
                      <div
                        data-testid="review-progress-bar"
                        className="h-full bg-coral rounded-full"
                        style={{ width: `${progressPct}%` }}
                      />
                    </div>
                  )}
                </div>

                {!singleMode && (
                  <>
                    <div className="h-5 w-px bg-softBorder hidden md:block" />
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-ink">Window:</span>
                      <div className="inline-flex rounded-lg border border-softBorder bg-white overflow-hidden">
                        {WINDOW_OPTIONS.map((d) => (
                          <button
                            key={d}
                            onClick={() => setWindow(d)}
                            className={`px-2.5 py-1 text-sm font-medium transition-colors ${
                              windowDays === d ? 'bg-coral text-white' : 'text-inkMuted hover:bg-cream'
                            }`}
                          >
                            {d}d
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <label htmlFor="reviewLocationFilter" className="text-sm font-medium text-ink">
                        Location:
                      </label>
                      <select
                        id="reviewLocationFilter"
                        value={locationId}
                        onChange={(e) => setLocation(e.target.value)}
                        className="px-2 py-1 border border-softBorder rounded-md bg-white text-sm"
                      >
                        <option value="">All locations</option>
                        {locations.map((l) => (
                          <option key={l.id} value={l.id}>
                            {l.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* feat-186 — the queue-composition ("Mix") control REPLACES the
                        old "Only disagreements" checkbox (AC6). Its 100/0/0 preset
                        reproduces that behavior (AC2). Hidden for drink_promotion
                        (never judged → no buckets, R5/AC8). */}
                    {judged && (
                      <MixControl
                        shares={shares}
                        agreementMaxDays={agreementMaxDaysBack}
                        windowDays={windowDays}
                        excludeHumanReviewed={excludeHumanReviewed}
                        onCommitShares={setShares}
                        onChangeMaxDays={setAgreementMaxDays}
                        onChangeExcludeHumanReviewed={setExcludeHumanReviewed}
                      />
                    )}

                    {/* feat-188 — drink_promotion has NO composition popover
                        (MixControl is judged-only), but the filter DOES apply to its
                        plain human-review list (ADR D-2 / AC9). Rendering the same
                        control standalone here is what keeps D5's promise — "toggle
                        OFF to restore today's behavior" — reachable on this feed;
                        without it the filter would be ON with no way to turn it off
                        short of hand-editing the URL. Design-lead ruled placement
                        WITHIN the popover and did not reach the no-popover case; this
                        fills that gap with the same control and copy. */}
                    {!judged && (
                      <ExcludeReviewedCheckbox
                        variant="toolbar"
                        checked={excludeHumanReviewed}
                        onChange={setExcludeHumanReviewed}
                      />
                    )}

                    {/* feat-196 (AC10) — "Live clients only", carried in from the
                        dashboard link and shown here so its state is visible and
                        reversible on the page it lands on. Rendered for BOTH judged
                        and drink_promotion feeds (unlike the composition controls):
                        it is a population filter, the same reason
                        ExcludeReviewedCheckbox renders on both. */}
                    <label className="flex items-center gap-2 text-sm font-medium text-ink cursor-pointer">
                      <input
                        type="checkbox"
                        data-testid="live-only-toggle"
                        checked={liveOnly}
                        onChange={(e) => setLiveOnly(e.target.checked)}
                        className="h-4 w-4 rounded border-softBorder text-coral focus:ring-coral"
                      />
                      Live clients only
                    </label>

                    {/* D5 — "Only current prompt template" toggle (off by default),
                        standalone/general scope like Location. Judged-only. */}
                    {judged && (
                      <label className="flex items-center gap-2 text-sm font-medium text-ink cursor-pointer">
                        <input
                          type="checkbox"
                          checked={onlyCurrentTemplate}
                          onChange={(e) => setOnlyCurrentTemplate(e.target.checked)}
                          className="h-4 w-4 rounded border-softBorder text-coral focus:ring-coral"
                        />
                        Only current prompt template
                      </label>
                    )}
                  </>
                )}
              </div>

              {/* feat-186 (D8 / D-B) — the ACHIEVED composition of the loaded batch,
                  counted from each item's server-authoritative `bucket`. Shows what
                  actually landed on screen vs what was requested, so a thin bucket
                  back-filled per D8 is legible rather than a silent lie. Judged
                  feeds only, once rows exist. */}
              {judged && !singleMode && achieved && (
                <div
                  data-testid="achieved-composition"
                  className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-[11px] text-inkMuted"
                >
                  <span className="inline-flex h-2.5 w-20 rounded-sm overflow-hidden ring-1 ring-softBorder">
                    <MixSegments shares={achieved.shares} />
                  </span>
                  <span>
                    In view: {achieved.shares.discrepancy}/{achieved.shares.uncertain}/
                    {achieved.shares.agreement}
                  </span>
                  {achieved.degraded && (
                    <span className="text-ink">
                      · {BUCKET_LABEL[achieved.degraded.short].toLowerCase()} is thin —{' '}
                      {achieved.degraded.filledPct}% of its share filled from{' '}
                      {BUCKET_LABEL[achieved.degraded.from].toLowerCase()} (D8)
                    </span>
                  )}
                </div>
              )}
              {/* R5 — the shortcut legend matches the stack keys. */}
              <div className="text-[11px] text-inkMuted mt-1">
                Shortcuts: <span className="font-mono">J</span>/<span className="font-mono">K</span> or{' '}
                <span className="font-mono">↑</span>/<span className="font-mono">↓</span> move ·{' '}
                <span className="font-mono">C</span> correct · <span className="font-mono">I</span>{' '}
                incorrect · <span className="font-mono">U</span> undecided ·{' '}
                <span className="font-mono">Enter</span> expand
              </div>
            </div>

            {isLoading ? (
              <FullPageSpinner />
            ) : items.length === 0 ? (
              <div
                data-testid="review-empty-state"
                className="p-8 text-center text-sm text-inkMuted bg-white border border-softBorder rounded-xl"
              >
                {singleMode ? (
                  'That interaction was not found (it may be from another tenant or no longer exist).'
                ) : (
                  <>
                    {/* The MESSAGE names the NARROWEST cause of the emptiness. A
                        100/0/0 mix is more specific than the population filter, so it
                        keeps its feat-186 wording (AC2/AC6) even when the filter is
                        also on; otherwise the filter is the salient cause and AC8's
                        "No un-reviewed …" copy applies. */}
                    {judged && shares.discrepancy === 100
                      ? // 100/0/0 = the old "Only disagreements" behavior (AC2/AC6).
                        `No ${prettyType(interactionType).toLowerCase()} discrepancies in this window.`
                      : excludeHumanReviewed
                      ? `No un-reviewed ${prettyType(interactionType).toLowerCase()} interactions in this window.`
                      : `No ${judged ? 'judged ' : ''}${prettyType(interactionType).toLowerCase()} interactions in this window.`}
                    {/* feat-188 (AC8) — the escape hatch is offered whenever the filter
                        is on, INDEPENDENT of which message won above: default-ON is a
                        behavior change, so an empty page must never be a dead end that
                        leaves the reviewer wondering where their work went. */}
                    {excludeHumanReviewed && (
                      <>
                        <br />
                        <button
                          type="button"
                          data-testid="empty-show-reviewed"
                          onClick={() => setExcludeHumanReviewed(false)}
                          className="mt-2 text-coral underline"
                        >
                          Show already-reviewed interactions
                        </button>
                      </>
                    )}
                  </>
                )}
              </div>
            ) : (
              <>
                {/* Variant C — one continuous panel, hairline dividers, no per-card
                    chrome. The divider + the aligned click gutter carry "this is one
                    item"; the ~45px/row of border+radius+shadow+gap does not. */}
                <div
                  data-testid="review-stack"
                  className="bg-white border border-softBorder rounded-xl shadow-sm divide-y divide-softBorder overflow-hidden"
                >
                  {items.map((item, idx) => (
                    <StackRow
                      key={item.id}
                      item={item}
                      judged={judged}
                      busy={pendingId === item.id}
                      focused={idx === focusedIndex}
                      expanded={expandedIds.has(item.id)}
                      reopened={reopenedIds.has(item.id)}
                      dispositioned={dispositionedIds.has(item.id)}
                      decidedElsewhere={decidedElsewhereIds.has(item.id)}
                      error={rowErrors[item.id] ?? null}
                      onMark={(status) => markAt(idx, status)}
                      onToggleExpand={() => toggleExpand(item.id)}
                      onReopen={() => reopen(item.id)}
                      onFocus={() => setFocusedIndex(idx)}
                      rowRef={(el) => {
                        rowEls.current[item.id] = el;
                      }}
                    />
                  ))}

                  {/* R6 / AC9 — the paging sentinel lives INSIDE the panel with a
                      RESERVED fixed height, so appending page 2 never shifts the
                      rows already on screen. */}
                  {!singleMode && hasMore && (
                    <div
                      ref={sentinelRef}
                      data-testid="paging-sentinel"
                      /* The re-subscription signal, exposed so it can be asserted.
                         jsdom has no IntersectionObserver — the component falls back
                         to the visible button — so the observer itself cannot be
                         exercised here, and a test that counted observers would be
                         measuring React's effect-flush timing rather than the
                         behaviour. What IS observable, and is the property that
                         matters, is that this counter advances on a page that renders
                         nothing. If it stops advancing, auto-load stops re-subscribing.
                         The live observer is QA's AC10 real-page check. */
                      data-pages-loaded={pagesLoaded}
                      className="h-[76px] flex flex-col items-center justify-center gap-2 bg-white"
                    >
                      {loadMoreFailed ? (
                        <>
                          <div className="text-sm text-red-700">Couldn't load the next page.</div>
                          <button
                            type="button"
                            onClick={() => {
                              setLoadMoreFailed(false);
                              loadMore();
                            }}
                            className="text-[11px] text-coral underline"
                          >
                            Load more
                          </button>
                        </>
                      ) : isLoadingMore || canAutoLoad ? (
                        <>
                          <div className="flex items-center gap-2 text-sm text-inkMuted">
                            <span className="inline-block w-3.5 h-3.5 rounded-full border-2 border-coral border-t-transparent animate-spin" />
                            Loading the next {PAGE_SIZE}…
                          </div>
                          {!canAutoLoad && (
                            <button
                              type="button"
                              onClick={() => loadMore()}
                              className="text-[11px] text-coral underline"
                            >
                              Load more
                            </button>
                          )}
                        </>
                      ) : (
                        <button
                          type="button"
                          onClick={() => loadMore()}
                          className="text-sm text-coral underline"
                        >
                          Load more
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {!singleMode && allReviewed && (
                  <div
                    data-testid="all-reviewed-banner"
                    className="mt-4 p-4 text-center text-sm text-green-700 bg-green-50 border border-green-200 rounded-xl"
                  >
                    {/* feat-188 — mirrors the pill's verb. With the filter ON the set
                        was an un-reviewed worklist, so "cleared" is the honest word
                        and "in this batch" is the honest scope: more un-reviewed work
                        may exist outside the loaded pages. */}
                    All {items.length} {prettyType(interactionType).toLowerCase()} interactions in this{' '}
                    {excludeHumanReviewed ? 'batch cleared' : 'window reviewed'}. 🎉
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default InteractionTypeReview;
